import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import BackOfficeMenu from '../components/BackOfficeMenu';
import FournisseurDocumentFormPage from './FournisseurDocumentFormPage';
import FournisseurDocumentDetailPage from './FournisseurDocumentDetailPage';
import HistoriquePrixModal from './HistoriquePrixModal';
import { PAYMENT_MODES } from '../../utils/paymentModes';
import './DetailFournisseur.css';

const API_URL = import.meta.env.VITE_API_URL;

const DetailFournisseur = ({ fournisseurId, dashboardAction, sourceBlId, sourceBcId, onNavigate, currentPage }) => {
  const [fournisseur, setFournisseur] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Produits liés au fournisseur
  const [produitsFournisseur, setProduitsFournisseur] = useState([]);
  // Documents (BC / BL) émis pour ce fournisseur
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDetailDoc, setSelectedDetailDoc] = useState(null);

  // --- Modales existantes ---
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [showHistoryPriceModal, setShowHistoryPriceModal] = useState(false);

  // --- Modale Créer document (BC / BL) ---
  const [showDocModal, setShowDocModal] = useState(false);
  const [docType, setDocType] = useState('bon_commande'); // 'bon_commande' | 'bon_livraison'
  // { produitId: quantite } pour gérer qté par produit
  const [produitsQte, setProduitsQte] = useState({});
  const [docDescription, setDocDescription] = useState('');
  const [docNotes, setDocNotes] = useState('');
  const [docConditions, setDocConditions] = useState('');
  const [docFrais, setDocFrais] = useState([]);
  const [docSubmitting, setDocSubmitting] = useState(false);
  const [conversionBonCommandeId, setConversionBonCommandeId] = useState(null);
  const [sendBonCommandeEmail, setSendBonCommandeEmail] = useState(false);
  const [docPageContext, setDocPageContext] = useState(null);
  const [showPaletteImportModal, setShowPaletteImportModal] = useState(false);
  const [paletteImportDoc, setPaletteImportDoc] = useState(null);
  const [paletteImportFile, setPaletteImportFile] = useState(null);
  const [paletteImportFileName, setPaletteImportFileName] = useState('');
  const [paletteImportPreview, setPaletteImportPreview] = useState([]);
  const [paletteImporting, setPaletteImporting] = useState(false);

  // --- Modale Email ---
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailDocId, setEmailDocId] = useState(null);
  const [emailDocNumero, setEmailDocNumero] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailObject, setEmailObject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [historiqueFilter, setHistoriqueFilter] = useState('all');
  const [handledDashboardAction, setHandledDashboardAction] = useState('');

  // --- Factures fournisseur (formulaire inline) ---
  const [factures, setFactures] = useState([]);
  const [loadingFactures, setLoadingFactures] = useState(false);
  const [showFactureForm, setShowFactureForm] = useState(false);
  const [showEcheanceModal, setShowEcheanceModal] = useState(false);
  const [showModifierEcheanceModal, setShowModifierEcheanceModal] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [selectedEcheance, setSelectedEcheance] = useState(null);
  const [modifierEcheanceForm, setModifierEcheanceForm] = useState({
    montant: '',
    methode: 'virement',
    dateEcheance: '',
    commentaire: ''
  });
  const [factureForm, setFactureForm] = useState({
    numeroFacture: '',
    dateFacture: '',
    dateReception: '',
    bonsLivraison: [],
    montantHT: 0,
    tva: 0,
    montantTTC: 0,
    fodecApplicable: false,
    tauxFodec: 1,
    timbreFiscalApplicable: true,
    acompteMontant: 0,
    acompteDate: '',
    acompteMethode: 'virement',
    acompteReference: '',
    acompteNomRecepteur: '',
    modePaiementReste: 'comptant',
    typeReglement: 'virement',
    nbEcheances: 1,
    periodiciteMois: 1,
    datePremiereEcheance: ''
  });
  const [submittingFacture, setSubmittingFacture] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [echeanceForm, setEcheanceForm] = useState({
    reference: '',
    datePaiement: '',
    banque: '',
    pieceJointe: '',
    commentaire: '',
    methode: 'virement',
    nomRecepteur: ''
  });

  // --- Formulaires existants ---
  const [evaluationData, setEvaluationData] = useState({ qualite: 0, prix: 0, delai: 0, service: 0 });
  const [historiqueData, setHistoriqueData] = useState({ type: 'Commande', titre: '', description: '', montant: '', statut: 'En cours' });

  // ── Helpers ──────────────────────────────────────────────
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const formatCurrency = (value) =>
    new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getEvaluationMoyenne = () => {
    if (!fournisseur) return 0;
    const { qualite, prix, delai, service } = fournisseur.evaluation;
    return ((qualite + prix + delai + service) / 4).toFixed(1);
  };

  const getTypeIcon = (type) => {
    const icons = { Commande: 'shopping-cart', Paiement: 'money-bill-wave', Livraison: 'truck', Réclamation: 'exclamation-triangle', Contact: 'phone', Évaluation: 'star', Modification: 'edit', Autre: 'info-circle' };
    return icons[type] || 'info-circle';
  };
  const getTypeColor = (type) => {
    const colors = { Commande: '#17a2b8', Paiement: '#28a745', Livraison: '#ff8c00', Réclamation: '#dc3545', Contact: '#6f42c1', Évaluation: '#ffc107', Modification: '#6c757d', Autre: '#17a2b8' };
    return colors[type] || '#6c757d';
  };
  const docTypeLabel = (t) => t === 'bon_commande' ? 'Bon de Commande' : 'Bon de Livraison';
  const canEditOrDeleteDocument = (doc) => Boolean(doc && doc.statut !== 'supprime');

  const isProduitLineaire = (produit) => {
    if (!produit) return false;
    return produit.modeGestionStock === 'lineaire';
  };

  const formatStockQuantity = (quantite, isLineaire = false) => {
    const num = Number(quantite || 0);
    const formatted = num.toLocaleString('fr-FR').replace(/\s/g, '\u00A0');
    return `${formatted}${isLineaire ? '\u00A0m' : ''}`;
  };

  const getPrixUnitaireAchat = (produit) => {
    if (!produit) return 0;
    if (isProduitLineaire(produit)) {
      const prixMetre = Number(produit.prixMetre) || 0;
      if (prixMetre > 0) return prixMetre;
    }
    return Number(produit.prixAchat) || 0;
  };

  const buildDefaultSelection = (produit) => {
    if (isProduitLineaire(produit)) {
      return {
        mode: 'lineaire',
        decoupes: [{ nombrePieces: 1, longueurPieceMetre: 1 }],
        metres: 1
      };
    }

    return {
      mode: 'unite',
      qte: 1
    };
  };

  const normalizeDecoupe = (decoupe) => {
    const nombrePieces = Math.max(1, parseInt(decoupe && decoupe.nombrePieces, 10) || 1);
    const longueurPieceMetre = Math.max(0.001, Number(decoupe && decoupe.longueurPieceMetre) || 0.001);
    return {
      nombrePieces,
      longueurPieceMetre,
      metres: Number((nombrePieces * longueurPieceMetre).toFixed(3))
    };
  };

  const recalculateLineaireEntry = (entry) => {
    const baseDecoupes = Array.isArray(entry && entry.decoupes) && entry.decoupes.length > 0
      ? entry.decoupes
      : [{ nombrePieces: entry && entry.nombrePieces, longueurPieceMetre: entry && entry.longueurPieceMetre }];

    const decoupes = baseDecoupes.map(normalizeDecoupe);
    const metres = Number(decoupes.reduce((sum, d) => sum + d.metres, 0).toFixed(3));

    return {
      mode: 'lineaire',
      decoupes,
      metres
    };
  };

  const getTypeLabels = (f) => {
    if (!f || !Array.isArray(f.typeProduit) || f.typeProduit.length === 0) {
      return 'Non défini';
    }
    return f.typeProduit.map(t => t.label || t).join(', ');
  };

  // ── Fetch data ────────────────────────────────────────────
  useEffect(() => {
    fetchFournisseur();
    fetchHistorique();
    fetchProduitsFournisseur();
    fetchDocumentsFournisseur();
    fetchFacturesFournisseur();
  }, [fournisseurId]);

  const fetchFournisseur = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/fournisseurs/${fournisseurId}`, { headers: authHeader() });
      setFournisseur(res.data);
      setEvaluationData(res.data.evaluation);
      setError(null);
    } catch {
      setError('Erreur lors du chargement du fournisseur');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorique = async () => {
    try {
      const res = await axios.get(`${API_URL}/fournisseurs/${fournisseurId}/historique`, { headers: authHeader() });
      setHistorique(res.data);
    } catch {}
  };

  const fetchProduitsFournisseur = async () => {
    try {
      const res = await axios.get(`${API_URL}/produits?fournisseur=${fournisseurId}`, { headers: authHeader() });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setProduitsFournisseur(list);
    } catch {}
  };

  const fetchDocumentsFournisseur = async () => {
    try {
      setLoadingDocs(true);
      // On récupère tous les documents financiers et on filtre côté client
      // (pas d'endpoint dédié fournisseur, mais on peut passer un query)
      const res = await axios.get(`${API_URL}/documents-financiers/fournisseur/${fournisseurId}`, { headers: authHeader() });
      setDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch {
      // Silencieux si endpoint non disponible
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchFacturesFournisseur = async () => {
    try {
      setLoadingFactures(true);
      const res = await axios.get(`${API_URL}/factures-fournisseurs/fournisseur/${fournisseurId}`, { headers: authHeader() });
      setFactures(Array.isArray(res.data?.factures) ? res.data.factures : []);
    } catch {
      setFactures([]);
    } finally {
      setLoadingFactures(false);
    }
  };

  // ── Évaluation ────────────────────────────────────────────
  const handleEvaluationSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/fournisseurs/${fournisseurId}/evaluation`, evaluationData, { headers: authHeader() });
      setShowEvaluationModal(false);
      fetchFournisseur();
      fetchHistorique();
      showSuccess('Évaluation mise à jour avec succès');
    } catch {
      setError('Erreur lors de la mise à jour de l\'évaluation');
    }
  };

  // ── Historique manuel ─────────────────────────────────────
  const handleHistoriqueSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fournisseurs/${fournisseurId}/historique`, historiqueData, { headers: authHeader() });
      setShowHistoriqueModal(false);
      setHistoriqueData({ type: 'Commande', titre: '', description: '', montant: '', statut: 'En cours' });
      fetchFournisseur();
      fetchHistorique();
      showSuccess('Entrée ajoutée à l\'historique');
    } catch {
      setError('Erreur lors de l\'ajout à l\'historique');
    }
  };

  // ── Utilitaires ───────────────────────────────────────────
  const showSuccess = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3000);
  };

  const resetFactureForm = () => {
    setFactureForm({
      isEditingFacture: false,
      editingFactureId: null,
      numeroFacture: '',
      dateFacture: '',
      dateReception: '',
      bonsLivraison: [],
      remise: 0,
      remise2Mode: 'aucun',
      remise2Val: 0,
      remise2Type: 'pourcentage',
      montantHT: 0,
      tva: 0,
      montantTTC: 0,
      acompteMontant: 0,
      acompteDate: '',
      acompteMethode: 'virement',
      acompteReference: '',
      acompteNomRecepteur: '',
      modePaiementReste: 'comptant',
      typeReglement: 'virement',
      nbEcheances: 1,
      periodiciteMois: 1,
      datePremiereEcheance: ''
    });
  };

  const openFactureForm = () => {
    resetFactureForm();
    setShowFactureForm(true);
  };

  const closeFactureForm = () => {
    setShowFactureForm(false);
    resetFactureForm();
  };

  const openEditFactureForm = (facture) => {
    setFactureForm({
      isEditingFacture: true,
      editingFactureId: facture._id,
      numeroFacture: facture.numeroFacture || '',
      dateFacture: facture.dateFacture ? new Date(facture.dateFacture).toISOString().split('T')[0] : '',
      dateReception: facture.dateReception ? new Date(facture.dateReception).toISOString().split('T')[0] : '',
      bonsLivraison: Array.isArray(facture.bonsLivraison) ? facture.bonsLivraison.map(bl => String(bl?._id || bl)) : [],
      remise: facture.remise || 0,
      remise2Mode: facture.remise2Mode || 'aucun',
      remise2Val: facture.remise2Val || 0,
      remise2Type: facture.remise2Type || 'pourcentage',
      fodecApplicable: facture.fodecApplicable ?? false,
      tauxFodec: facture.tauxFodec ?? 1,
      timbreFiscalApplicable: facture.timbreFiscalApplicable ?? true,
      montantHT: facture.montantHT || 0,
      tva: facture.tva || 0,
      montantTTC: facture.montantTTC || 0,
      acompteMontant: facture.acompte?.montant || 0,
      acompteDate: facture.acompte?.datePaiement ? new Date(facture.acompte.datePaiement).toISOString().split('T')[0] : '',
      acompteMethode: facture.acompte?.methode || 'virement',
      acompteReference: facture.acompte?.reference || '',
      acompteNomRecepteur: facture.acompte?.nomRecepteur || '',
      modePaiementReste: facture.modePaiementReste || 'comptant',
      typeReglement: facture.typeReglement || 'virement',
      nbEcheances: facture.planification?.nbEcheances || 1,
      periodiciteMois: facture.planification?.periodiciteMois || 1,
      datePremiereEcheance: facture.planification?.datePremiereEcheance ? new Date(facture.planification.datePremiereEcheance).toISOString().split('T')[0] : ''
    });
    setShowFactureForm(true);
  };

  const openFactureFormFromBL = (blId) => {
    const totals = computeTotalsFromBL([blId], 'aucun', 0, 'pourcentage');
    const today = new Date().toISOString().slice(0, 10);
    setFactureForm(prev => ({
      ...prev,
      isEditingFacture: false,
      editingFactureId: null,
      numeroFacture: '',
      dateFacture: today,
      dateReception: today,
      bonsLivraison: [blId],
      remise: 0,
      remise2Mode: 'aucun',
      remise2Val: 0,
      remise2Type: 'pourcentage',
      montantHT: totals.montantHT,
      tva: totals.tva,
      montantTTC: totals.montantTTC,
      acompteMontant: 0,
      acompteDate: '',
      acompteMethode: 'virement',
      acompteReference: '',
      acompteNomRecepteur: '',
      modePaiementReste: 'comptant',
      typeReglement: 'virement',
      nbEcheances: 1,
      periodiciteMois: 1,
      datePremiereEcheance: today
    }));
    setShowFactureForm(true);
  };

  const computeTotalsFromBL = (ids, modeR2 = factureForm?.remise2Mode, valR2 = factureForm?.remise2Val, typeR2 = factureForm?.remise2Type,
                               fodecApplicable = factureForm?.fodecApplicable, tauxFodec = factureForm?.tauxFodec, timbreFiscalApplicable = factureForm?.timbreFiscalApplicable) => {
    const selected = documents.filter(doc => ids.includes(doc._id));
    const totals = selected.reduce((acc, doc) => {
      acc.montantHT += Number(doc.montantHT) || 0;
      acc.tva += Number(doc.tva) || 0;
      acc.montantTTC += Number(doc.montantTTC || doc.montant) || 0;
      return acc;
    }, { montantHT: 0, tva: 0, montantTTC: 0 });

    let finalHT = totals.montantHT;
    let finalTVA = totals.tva;
    const r2Val = Math.max(0, Number(valR2) || 0);

    if (modeR2 === 'global' && r2Val > 0) {
      let r2Amt = 0;
      if (typeR2 === 'montant') {
        r2Amt = Math.min(totals.montantHT, r2Val);
      } else {
        r2Amt = totals.montantHT * (Math.min(100, r2Val) / 100);
      }
      finalHT = Math.max(0, totals.montantHT - r2Amt);
      if (totals.montantHT > 0) {
        finalTVA = totals.tva * (finalHT / totals.montantHT);
      }
    }
    
    let fodecAmount = 0;
    if (fodecApplicable) {
      fodecAmount = finalHT * ((Number(tauxFodec) || 1) / 100);
    }
    
    if (fodecApplicable && finalHT > 0) {
      const fodecRatio = fodecAmount / finalHT;
      finalTVA = finalTVA * (1 + fodecRatio);
    }

    let timbreAmount = 0;
    if (timbreFiscalApplicable) {
      timbreAmount = 1.000;
    }

    return {
      montantHT: Number(finalHT.toFixed(3)),
      fodecAmount: Number(fodecAmount.toFixed(3)),
      tva: Number(finalTVA.toFixed(3)),
      montantTTC: Number((finalHT + fodecAmount + finalTVA + timbreAmount).toFixed(3))
    };
  };

  const handleFactureChange = (e) => {
    const target = e.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    
    setFactureForm(prev => {
      const next = { ...prev, [name]: value };
      if (['remise2Mode', 'remise2Val', 'remise2Type', 'fodecApplicable', 'tauxFodec', 'timbreFiscalApplicable'].includes(name) && prev.bonsLivraison.length > 0) {
        const modeR2 = name === 'remise2Mode' ? value : prev.remise2Mode;
        const valR2 = name === 'remise2Val' ? value : prev.remise2Val;
        const typeR2 = name === 'remise2Type' ? value : prev.remise2Type;
        const fodecApp = name === 'fodecApplicable' ? value : prev.fodecApplicable;
        const txFodec = name === 'tauxFodec' ? value : prev.tauxFodec;
        const timbreApp = name === 'timbreFiscalApplicable' ? value : prev.timbreFiscalApplicable;
        
        const totals = computeTotalsFromBL(prev.bonsLivraison, modeR2, valR2, typeR2, fodecApp, txFodec, timbreApp);
        return {
          ...next,
          montantHT: totals.montantHT,
          tva: totals.tva,
          montantTTC: totals.montantTTC
        };
      }
      return next;
    });
  };

  const handleSelectBonLivraison = (id, checked) => {
    setFactureForm(prev => {
      const nextIds = checked
        ? Array.from(new Set([...prev.bonsLivraison, id]))
        : prev.bonsLivraison.filter(x => x !== id);

      const totals = computeTotalsFromBL(nextIds, prev.remise2Mode, prev.remise2Val, prev.remise2Type);
      return {
        ...prev,
        bonsLivraison: nextIds,
        montantHT: totals.montantHT,
        tva: totals.tva,
        montantTTC: totals.montantTTC
      };
    });
  };

  const handleCreateFacture = async (e) => {
    e.preventDefault();
    if (!factureForm.numeroFacture) {
      Swal.fire('Attention', 'Numéro de facture requis.', 'warning');
      return;
    }
    if (factureForm.bonsLivraison.length === 0) {
      Swal.fire('Attention', 'Sélectionnez au moins un bon de livraison.', 'warning');
      return;
    }
    if (Number(factureForm.acompteMontant) > 0 && !factureForm.acompteDate) {
      Swal.fire('Attention', 'La date de l\'acompte est obligatoire.', 'warning');
      return;
    }
    if (Number(factureForm.acompteMontant) > 0 && factureForm.acompteMethode === 'especes' && !(factureForm.acompteNomRecepteur || '').trim()) {
      Swal.fire('Attention', 'Nom du recepteur obligatoire pour un acompte en espèces.', 'warning');
      return;
    }

    setSubmittingFacture(true);
    try {
      const payload = {
        fournisseurId,
        numeroFacture: factureForm.numeroFacture,
        dateFacture: factureForm.dateFacture,
        dateReception: factureForm.dateReception,
        bonsLivraison: factureForm.bonsLivraison,
        remise: Number(factureForm.remise) || 0,
        remise2Mode: factureForm.remise2Mode,
        remise2Val: Number(factureForm.remise2Val) || 0,
        remise2Type: factureForm.remise2Type,
        fodecApplicable: factureForm.fodecApplicable,
        tauxFodec: Number(factureForm.tauxFodec) || 1,
        timbreFiscalApplicable: factureForm.timbreFiscalApplicable,
        montantHT: Number(factureForm.montantHT) || 0,
        tva: Number(factureForm.tva) || 0,
        montantTTC: Number(factureForm.montantTTC) || 0,
        acompte: {
          montant: Number(factureForm.acompteMontant) || 0,
          datePaiement: factureForm.acompteDate,
          methode: factureForm.acompteMethode,
          reference: factureForm.acompteReference,
          nomRecepteur: factureForm.acompteNomRecepteur
        },
        modePaiementReste: factureForm.modePaiementReste,
        typeReglement: factureForm.typeReglement,
        planification: {
          nbEcheances: Number(factureForm.nbEcheances) || 1,
          periodiciteMois: Number(factureForm.periodiciteMois) || 1,
          datePremiereEcheance: factureForm.datePremiereEcheance,
          datePaiement: factureForm.datePremiereEcheance
        }
      };

      const res = factureForm.isEditingFacture
        ? await axios.patch(`${API_URL}/factures-fournisseurs/${factureForm.editingFactureId}`, payload, { headers: authHeader() })
        : await axios.post(`${API_URL}/factures-fournisseurs`, payload, { headers: authHeader() });
      const data = res.data;

      // Afficher les avertissements non-bloquants (écarts avec BL)
      const warnings = data?.facture?.verification?.details;
      if (Array.isArray(warnings) && warnings.length > 0) {
        await Swal.fire({
          title: `Facture ${factureForm.isEditingFacture ? 'modifiée' : 'créée'} avec des avertissements`,
          html: `<p style="color:#92400e;margin-bottom:10px"><i class="fas fa-exclamation-triangle"></i> Des écarts ont été détectés par rapport aux bons de livraison :</p><ul style="text-align:left;padding-left:20px">${warnings.map(w => `<li>${w}</li>`).join('')}</ul>`,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      } else {
        showSuccess(`Facture fournisseur ${factureForm.isEditingFacture ? 'modifiée' : 'créée'} avec succès`);
      }

      setShowFactureForm(false);
      resetFactureForm();
      fetchFacturesFournisseur();
      fetchDocumentsFournisseur();
    } catch (err) {
      const errorMsg = err.response?.data?.message || `Erreur lors de ${factureForm.isEditingFacture ? 'la modification' : 'la création'} de la facture`;
      const details = Array.isArray(err.response?.data?.errors)
        ? err.response.data.errors.join('\n')
        : '';
      Swal.fire('Erreur', `${errorMsg}${details ? `\n${details}` : ''}`, 'error');
    } finally {
      setSubmittingFacture(false);
    }
  };

  const handleArchiveFacture = async (facture) => {
    const result = await Swal.fire({
      title: 'Archiver cette facture ? ',
      text: `Facture ${facture.numeroFacture} sera archivee.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, archiver',
      cancelButtonText: 'Annuler'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.patch(`${API_URL}/factures-fournisseurs/${facture._id}/archiver`, {}, { headers: authHeader() });
      fetchFacturesFournisseur();
      showSuccess('Facture archivee');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de l\'archivage';
      Swal.fire('Erreur', errorMsg, 'error');
    }
  };

  const handleAnnulerFacture = async (facture) => {
    const result = await Swal.fire({
      title: 'Annuler cette facture ? ',
      text: `Facture ${facture.numeroFacture} sera annulee.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Annuler'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.patch(`${API_URL}/factures-fournisseurs/${facture._id}/annuler`, {}, { headers: authHeader() });
      fetchFacturesFournisseur();
      showSuccess('Facture annulee');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de l\'annulation';
      Swal.fire('Erreur', errorMsg, 'error');
    }
  };

  const openEcheanceModal = (facture, echeance) => {
    setSelectedFacture(facture);
    setSelectedEcheance(echeance);
    setEcheanceForm({
      reference: '',
      datePaiement: '',
      banque: '',
      pieceJointe: '',
      commentaire: '',
      methode: echeance?.methode || 'virement',
      nomRecepteur: ''
    });
    setShowEcheanceModal(true);
  };

  const handlePayEcheance = async (e) => {
    e.preventDefault();
    if (!selectedFacture || !selectedEcheance) return;
    if (echeanceForm.methode === 'especes' && !(echeanceForm.nomRecepteur || '').trim()) {
      Swal.fire('Attention', 'Nom du recepteur obligatoire pour un paiement en espèces.', 'warning');
      return;
    }
    setSubmittingPayment(true);
    try {
      const formData = new FormData();
      formData.append('reference', echeanceForm.reference || '');
      formData.append('datePaiement', echeanceForm.datePaiement || '');
      formData.append('banque', echeanceForm.banque || '');
      formData.append('commentaire', echeanceForm.commentaire || '');
      formData.append('methode', echeanceForm.methode || 'virement');
      formData.append('nomRecepteur', echeanceForm.nomRecepteur || '');
      if (echeanceForm.pieceJointeFile) {
        formData.append('pieceJointe', echeanceForm.pieceJointeFile);
      }

      await axios.post(
        `${API_URL}/factures-fournisseurs/${selectedFacture._id}/echeances/${selectedEcheance._id}/payer`,
        formData,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
      );

      setShowEcheanceModal(false);
      fetchFacturesFournisseur();
      showSuccess('Échéance payée');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du paiement';
      Swal.fire('Erreur', errorMsg, 'error');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const openModifierEcheanceModal = (facture, echeance) => {
    setSelectedFacture(facture);
    setSelectedEcheance(echeance);
    setModifierEcheanceForm({
      montant: echeance.montant || '',
      methode: echeance.methode || 'virement',
      dateEcheance: echeance.dateEcheance
        ? new Date(echeance.dateEcheance).toISOString().split('T')[0]
        : '',
      commentaire: ''
    });
    setShowModifierEcheanceModal(true);
  };

  const handleModifierEcheance = async (e) => {
    e.preventDefault();
    if (!selectedFacture || !selectedEcheance) return;
    try {
      await axios.patch(
        `${API_URL}/factures-fournisseurs/${selectedFacture._id}/echeances/${selectedEcheance._id}`,
        {
          montant: Number(modifierEcheanceForm.montant),
          methode: modifierEcheanceForm.methode,
          dateEcheance: modifierEcheanceForm.dateEcheance,
          commentaire: modifierEcheanceForm.commentaire
        },
        { headers: authHeader() }
      );
      setShowModifierEcheanceModal(false);
      fetchFacturesFournisseur();
      showSuccess('Échéance modifiée avec succès');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la modification';
      Swal.fire('Erreur', errorMsg, 'error');
    }
  };

  // ── Créer Document (BC / BL) ──────────────────────────────
  const openDocModal = (type) => {
    if (type === 'bon_livraison') {
      Swal.fire('Action indisponible', 'Un Bon de Livraison fournisseur doit etre cree depuis un Bon de Commande valide.', 'info');
      return;
    }
    setDocPageContext({
      initialType: type,
      initialDoc: null,
      initialProduitsQte: {}
    });
  };

  // Toggle produit + gestion quantité
  const handleToggleProduit = (produit) => {
    const id = produit && produit._id ? produit._id : null;
    if (!id) return;

    setProduitsQte(prev => {
      if (prev[id] !== undefined) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: buildDefaultSelection(produit) };
    });
  };

  const handleSetQte = (id, val) => {
    const q = Math.max(1, parseInt(val, 10) || 1);
    setProduitsQte(prev => {
      const current = prev[id] || { mode: 'unite', qte: 1 };
      return { ...prev, [id]: { ...current, mode: 'unite', qte: q } };
    });
  };

  const handleSetLineaireDecoupe = (id, decoupeIndex, field, value) => {
    setProduitsQte(prev => {
      const current = prev[id] || { mode: 'lineaire', decoupes: [{ nombrePieces: 1, longueurPieceMetre: 1 }], metres: 1 };
      const decoupes = Array.isArray(current.decoupes) && current.decoupes.length > 0
        ? [...current.decoupes]
        : [{ nombrePieces: current.nombrePieces || 1, longueurPieceMetre: current.longueurPieceMetre || 1 }];

      const safeIndex = Math.max(0, decoupeIndex);
      while (decoupes.length <= safeIndex) {
        decoupes.push({ nombrePieces: 1, longueurPieceMetre: 1 });
      }

      const target = { ...decoupes[safeIndex] };
      if (field === 'nombrePieces') {
        target.nombrePieces = Math.max(1, parseInt(value, 10) || 1);
      }
      if (field === 'longueurPieceMetre') {
        target.longueurPieceMetre = Math.max(0.001, Number(value) || 0.001);
      }
      decoupes[safeIndex] = target;

      const next = recalculateLineaireEntry({ decoupes });

      return { ...prev, [id]: next };
    });
  };

  const handleAddLineaireDecoupe = (id) => {
    setProduitsQte(prev => {
      const current = prev[id] || { mode: 'lineaire', decoupes: [{ nombrePieces: 1, longueurPieceMetre: 1 }], metres: 1 };
      const decoupes = Array.isArray(current.decoupes) && current.decoupes.length > 0
        ? [...current.decoupes]
        : [{ nombrePieces: 1, longueurPieceMetre: 1 }];

      decoupes.push({ nombrePieces: 1, longueurPieceMetre: 1 });

      return { ...prev, [id]: recalculateLineaireEntry({ decoupes }) };
    });
  };

  const handleRemoveLineaireDecoupe = (id, decoupeIndex) => {
    setProduitsQte(prev => {
      const current = prev[id];
      if (!current) return prev;

      const decoupes = Array.isArray(current.decoupes) ? [...current.decoupes] : [];
      if (decoupes.length <= 1) {
        return prev;
      }

      decoupes.splice(decoupeIndex, 1);

      return { ...prev, [id]: recalculateLineaireEntry({ decoupes }) };
    });
  };

  // ── Validation d'un document BC (depuis la fiche fournisseur) ──────────────
  const handleValidateDocument = async (doc) => {
    const result = await Swal.fire({
      title: 'Valider ce document ?',
      text: `Le document ${doc.numero} sera validé. Cette action est irréversible.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, valider',
      cancelButtonText: 'Annuler'
    });
    if (!result.isConfirmed) return;
    try {
      await axios.patch(`${API_URL}/documents-financiers/${doc._id}/valider`, {}, { headers: authHeader() });
      fetchDocumentsFournisseur();
      showSuccess(`Document ${doc.numero} validé avec succès`);
    } catch (err) {
      Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la validation', 'error');
    }
  };

  const openPaletteImportModal = (doc) => {
    setPaletteImportDoc(doc);
    setPaletteImportFile(null);
    setPaletteImportFileName('');
    setPaletteImportPreview([]);
    setShowPaletteImportModal(true);
  };

  const closePaletteImportModal = () => {
    if (paletteImporting) return;
    setShowPaletteImportModal(false);
    setPaletteImportDoc(null);
    setPaletteImportFile(null);
    setPaletteImportFileName('');
    setPaletteImportPreview([]);
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const handlePaletteImportFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setPaletteImportFile(selectedFile);
    setPaletteImportFileName(selectedFile.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setPaletteImportPreview(jsonData.slice(0, 8));
      } catch (error) {
        setPaletteImportPreview([]);
        Swal.fire('Erreur', 'Impossible de lire le fichier Excel', 'error');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleImportPalettesBL = async () => {
    if (!paletteImportDoc || !paletteImportFile) {
      Swal.fire('Attention', 'Veuillez selectionner un fichier Excel.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', paletteImportFile);
    setPaletteImporting(true);

    try {
      const res = await axios.post(
        `${API_URL}/documents-financiers/fournisseur/bl/${paletteImportDoc._id}/import-palettes`,
        formData,
        {
          headers: {
            ...authHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const details = res.data?.details || {};
      await Swal.fire({
        icon: 'success',
        title: 'Palettes importees',
        html: `
          <div style="text-align:left">
            <p><strong>Lignes fichier :</strong> ${details.totalLines || 0}</p>
            <p><strong>Lignes importees :</strong> ${details.imported || 0}</p>
            <p><strong>Stock produit ajoute :</strong> 0 (deja compte par la validation BL)</p>
          </div>
        `,
        confirmButtonColor: '#1e3a5f'
      });

      setPaletteImporting(false);
      closePaletteImportModal();
      fetchDocumentsFournisseur();
      fetchHistorique();
    } catch (err) {
      const details = err.response?.data?.details;
      const errorDetails = Array.isArray(details?.errorDetails) ? details.errorDetails.slice(0, 8) : [];
      const errorsHtml = errorDetails.length > 0
        ? `<ul style="text-align:left; max-height:220px; overflow:auto; padding-left:18px;">
            ${errorDetails.map(item => `<li>Ligne ${escapeHtml(item.line || '-')}: ${escapeHtml((item.errors || []).join(', '))}</li>`).join('')}
          </ul>`
        : '';
      Swal.fire({
        icon: 'error',
        title: 'Import refuse',
        html: `<p>${escapeHtml(err.response?.data?.message || 'Erreur lors de l import des palettes')}</p>${errorsHtml}`,
        width: 680
      });
    } finally {
      setPaletteImporting(false);
    }
  };

  const bonsLivraisonFacturesIds = new Set(
    factures
      .filter(facture => facture.statut !== 'annule')
      .flatMap(facture => Array.isArray(facture.bonsLivraison) ? facture.bonsLivraison : [])
      .map(bl => String(bl?._id || bl))
  );
  const bonsLivraisonOptions = documents.filter(doc =>
    doc.type === 'bon_livraison' &&
    doc.statut === 'valide' &&
    !bonsLivraisonFacturesIds.has(String(doc._id))
  );

  const nbSelectionnes = Object.keys(produitsQte).length;

  // Calcul statut en_retard côté frontend (affichage immédiat sans attendre backend)
  const isEcheanceEnRetard = (ech) => {
    if (ech.statut !== 'en_attente') return false;
    return ech.dateEcheance && new Date(ech.dateEcheance) < new Date();
  };

  // Résumé financier pour une facture (utilise le champ calculé par le backend si dispo)
  const getFinancialSummary = (facture) => {
    if (facture._financialSummary) return facture._financialSummary;
    if (facture.montantPaye !== undefined || facture.montantRestant !== undefined) {
      return {
        totalPaye: Number(Number(facture.montantPaye || 0).toFixed(3)),
        resteARegler: Number(Number(facture.montantRestant || 0).toFixed(3)),
        statutPaiement: facture.statutPaiement
      };
    }
    const totalPaye = (facture.echeances || [])
      .reduce((s, e) => s + (e.statut === 'payee' ? (Number(e.montant) || 0) : (Number(e.montantPaye) || 0)), 0);
    const resteARegler = Math.max(0, (Number(facture.montantTTC) || 0) - totalPaye);
    return { totalPaye: Number(totalPaye.toFixed(3)), resteARegler: Number(resteARegler.toFixed(3)) };
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    if (nbSelectionnes === 0) {
      Swal.fire('Attention', 'Veuillez sélectionner au moins un produit.', 'warning');
      return;
    }
    if (docType === 'bon_livraison' && !conversionBonCommandeId) {
      Swal.fire('Action impossible', 'Un Bon de Livraison fournisseur doit etre cree depuis un Bon de Commande valide.', 'warning');
      return;
    }
    if (docType === 'bon_commande' && sendBonCommandeEmail && !fournisseur?.email) {
      Swal.fire('Email manquant', 'Ce fournisseur n a pas d email renseigne. Decochez l option ou ajoutez une adresse email.', 'warning');
      return;
    }
    setDocSubmitting(true);
    try {
      // Listes natives (axios sérialise en JSON automatiquement, pas besoin de JSON.stringify)
      const produitsAvecQte = Object.entries(produitsQte).map(([id, line]) => {
        if (line && line.mode === 'lineaire') {
          const computed = recalculateLineaireEntry(line);
          return {
            id,
            decoupes: computed.decoupes.map(d => ({
              nombrePieces: d.nombrePieces,
              longueurPieceMetre: d.longueurPieceMetre
            })),
            metres: computed.metres
          };
        }

        return {
          id,
          qte: Math.max(1, parseInt(line && line.qte, 10) || 1)
        };
      });

      const payload = {
        type: docType,
        description: docDescription,
        notes: docNotes,
        conditions: docConditions,
        // Envoi en Array natif — le backend gère string ou array
        fraisSupplementaires: docFrais,
        produitsSelectionnes:  produitsAvecQte.map(p => p.id),
        produitsAvecQuantites: produitsAvecQte,
      };

      if (docType === 'bon_livraison' && conversionBonCommandeId) {
        payload.bonCommandeRef = conversionBonCommandeId;
      }

      let docCreated = false;
      let createdDoc = null;
      try {
        const res = await axios.post(
          `${API_URL}/documents-financiers/fournisseur/${fournisseurId}`,
          payload,
          { headers: authHeader() }
        );
        docCreated = true;
        createdDoc = res.data.document;
      } catch (mainErr) {
        if (docType === 'bon_livraison') {
          throw mainErr;
        }
        console.warn('Endpoint fournisseur échoué, fallback historique :', mainErr.response?.data?.message);
        // Fallback : trace dans l'historique
        const desc = produitsAvecQte
          .map(({ id, qte, metres }) => {
            const p = produitsFournisseur.find(x => x._id === id);
            const label = metres ? `${metres} m` : `×${qte}`;
            return `${p?.nomProduit || id} ${label}`;
          })
          .join(', ');
        await axios.post(`${API_URL}/fournisseurs/${fournisseurId}/historique`, {
          type: 'Commande',
          titre: `${docTypeLabel(docType)} créé`,
          description: docDescription || desc,
          montant: produitsAvecQte.reduce((s, { id, qte, metres }) => {
            const p = produitsFournisseur.find(x => x._id === id);
            const prixUnitaire = getPrixUnitaireAchat(p);
            return s + prixUnitaire * (metres || qte || 0);
          }, 0),
          statut: 'En cours'
        }, { headers: authHeader() });
      }

      setShowDocModal(false);
      setConversionBonCommandeId(null);
      setSendBonCommandeEmail(false);
      fetchHistorique();
      fetchDocumentsFournisseur();

      if (docCreated && createdDoc) {
        let emailSent = false;
        if (docType === 'bon_commande' && sendBonCommandeEmail) {
          try {
            await axios.post(`${API_URL}/documents-financiers/${createdDoc._id}/envoyer-email`, {
              destinataire: fournisseur?.email || '',
              sujet: `Bon de Commande - ${createdDoc.numero}`,
              message: `Veuillez trouver ci-joint le Bon de Commande ${createdDoc.numero}.`
            }, { headers: authHeader() });
            emailSent = true;
          } catch (emailErr) {
            Swal.fire('Document cree', `Le Bon de Commande ${createdDoc.numero} a ete cree, mais l email n a pas pu etre envoye.`, 'warning');
          }
        }

        if (docType === 'bon_commande') {
          showSuccess(emailSent
            ? `Bon de Commande ${createdDoc.numero} cree et envoye par email`
            : `Bon de Commande ${createdDoc.numero} cree`
          );
          return;
        }

        // Afficher une petite carte avec les 3 boutons
        const result = await Swal.fire({
          title: `Document ${docTypeLabel(docType)} créé !`,
          html: `<p>Que souhaitez-vous faire avec le document <strong>${createdDoc.numero}</strong> ?</p>`,
          icon: 'success',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: '<i class="fas fa-envelope"></i> Envoyer Mail',
          denyButtonText: '<i class="fab fa-whatsapp"></i> WhatsApp',
          cancelButtonText: 'Annuler',
          confirmButtonColor: '#3085d6',
          denyButtonColor: '#25D366',
          cancelButtonColor: '#6c757d',
          customClass: {
            actions: 'df-swal-actions-vertical' // une petite classe potentielle pour styler les boutons si on veut
          }
        });

        if (result.isConfirmed) {
          openEmailModal(createdDoc);
        } else if (result.isDenied) {
          handleWhatsApp(createdDoc);
        }
      } else {
        showSuccess(`${docTypeLabel(docType)} enregistré en historique`);
      }
      
    } catch (err) {
      Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la création du document', 'error');
    } finally {
      setDocSubmitting(false);
    }
  };

  const buildConversionProduitsQte = (doc) => {
    if (!doc || !Array.isArray(doc.articles)) return {};

    const next = {};

    doc.articles.forEach((article) => {
      const produitId = article.produitId?._id || article.produitId;
      if (!produitId) return;

      const produit = produitsFournisseur.find(p => String(p._id) === String(produitId));
      const isLineaire = article.lineaire || (produit && produit.modeGestionStock === 'lineaire');

      if (isLineaire) {
        const rawDecoupes = Array.isArray(article.decoupes) && article.decoupes.length > 0
          ? article.decoupes
          : [{ nombrePieces: 1, longueurPieceMetre: Number(article.quantite) || 1 }];

        next[produitId] = recalculateLineaireEntry({ decoupes: rawDecoupes });
      } else {
        next[produitId] = {
          mode: 'unite',
          qte: Math.max(1, parseInt(article.quantite, 10) || 1)
        };
      }
    });

    return next;
  };

  const handleConvertToBonLivraison = (doc) => {
    const produitsQteFromDoc = buildConversionProduitsQte(doc);

    setDocPageContext({
      initialType: 'bon_livraison',
      initialDoc: doc,
      initialProduitsQte: produitsQteFromDoc,
      sourceBonCommandeId: doc._id
    });

    Swal.fire({
      title: 'Formulaire pre-rempli',
      text: `Vous creez un bon de livraison a partir de ${doc.numero}.`,
      icon: 'info',
      timer: 2500,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  };

  const handleDuplicateDocument = (doc) => {
    const produitsQteFromDoc = buildConversionProduitsQte(doc);

    setDocPageContext({
      initialType: doc.type === 'bon_livraison' ? 'bon_commande' : doc.type,
      initialDoc: doc,
      initialProduitsQte: produitsQteFromDoc,
      sourceBonCommandeId: null
    });

    Swal.fire({
      title: 'Formulaire pre-rempli',
      text: `Vous dupliquez le document ${doc.numero}.`,
      icon: 'info',
      timer: 2500,
      toast: true,
      position: 'top-end',
      showConfirmButton: false
    });
  };


  // ── Télécharger PDF ───────────────────────────────────────
  useEffect(() => {
    const actionKey = `${dashboardAction || ''}|${sourceBlId || ''}|${sourceBcId || ''}`;
    if (!dashboardAction || handledDashboardAction === actionKey || loadingDocs || documents.length === 0) return;

    if (dashboardAction === 'create_bl_from_bc' && sourceBcId) {
      const bc = documents.find(doc => String(doc._id) === String(sourceBcId) && doc.type === 'bon_commande');
      if (!bc) return;
      handleConvertToBonLivraison(bc);
      setHandledDashboardAction(actionKey);
      return;
    }

    if (dashboardAction === 'create_facture_from_bl' && sourceBlId) {
      const bl = documents.find(doc => String(doc._id) === String(sourceBlId) && doc.type === 'bon_livraison');
      if (!bl) return;
      openFactureFormFromBL(bl._id);
      setHandledDashboardAction(actionKey);
      Swal.fire({
        title: 'Facture pre-remplie',
        text: `Le BL ${bl.numero} est selectionne pour la facture fournisseur.`,
        icon: 'info',
        timer: 2500,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    }
  }, [dashboardAction, sourceBlId, sourceBcId, documents, loadingDocs, handledDashboardAction]);

  const handleEditDocument = (doc) => {
    if (!canEditOrDeleteDocument(doc)) {
      Swal.fire('Action impossible', 'Un document fournisseur valide ne peut plus etre modifie.', 'warning');
      return;
    }

    setDocPageContext({
      mode: 'edit',
      initialType: doc.type,
      initialDoc: doc,
      initialProduitsQte: null,
      sourceBonCommandeId: doc.type === 'bon_livraison'
        ? (doc.bonCommandeRef?._id || doc.bonCommandeRef || null)
        : null
    });
  };

  const handleDownloadPDF = async (docId, numero) => {
    try {
      const res = await axios.get(`${API_URL}/documents-financiers/${docId}/pdf`, {
        headers: authHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      Swal.fire('Erreur', 'Impossible de télécharger le PDF', 'error');
    }
  };

  // ── Email ─────────────────────────────────────────────────
  const openEmailModal = (doc, isRappel = false) => {
    setEmailDocId(doc._id);
    setEmailDocNumero(doc.numero);
    setEmailTo(fournisseur?.email || '');
    
    const objetMail = isRappel 
      ? `RAPPEL – ${docTypeLabel(doc.type)} N° ${doc.numero}`
      : `Document ${docTypeLabel(doc.type)} – N° ${doc.numero}`;
    
    setEmailObject(objetMail);
    
    setEmailBody(
      `Bonjour ${fournisseur?.prenomContact || ''} ${fournisseur?.nomContact || ''},\n\n` +
      `${isRappel ? 'Sauf erreur ou omission de notre part, nous vous rappelons ' : 'Veuillez trouver ci-joint '}le ${docTypeLabel(doc.type)} N° ${doc.numero}.\n\n` +
      `Cordialement,\nSASTRA Energy`
    );
    setShowEmailModal(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setEmailSending(true);
    try {
      await axios.post(`${API_URL}/documents-financiers/${emailDocId}/envoyer-email`, {
        destinataire: emailTo,
        sujet: emailObject,
        message: emailBody
      }, { headers: authHeader() });
      setShowEmailModal(false);
      showSuccess('Email envoyé avec succès');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de l\'envoi de l\'email';
      Swal.fire('Erreur', errorMsg, 'error');
    } finally {
      setEmailSending(false);
    }
  };

  // ── Suppression Document ──────────────────────────────────
  const handleDeleteDocument = async (docId, numero) => {
    const confirm = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: `Vous allez supprimer le document ${numero} et son fichier PDF de façon permanente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/documents-financiers/${docId}`, { headers: authHeader() });
        showSuccess(`Le document ${numero} a été supprimé.`);
        // Rafraîchir l'historique et les documents
        fetchDocumentsFournisseur();
        fetchHistorique();
      } catch (err) {
        Swal.fire('Erreur', err.response?.data?.message || 'Impossible de supprimer ce document', 'error');
      }
    }
  };


  // ── WhatsApp ──────────────────────────────────────────────
  const handleWhatsApp = (doc) => {
    Swal.fire({
      title: '📱 Envoi WhatsApp',
      html: `
        <div style="text-align:left; line-height:1.8; color:#2c3e50;">
          <p>🚧 <strong>Fonctionnalité en cours de développement</strong></p>
          <p>L'envoi automatique par WhatsApp sera disponible dans une prochaine version.</p>
          <hr style="margin:10px 0; border: 1px solid #eee;" />
          <p><strong>Document :</strong> ${docTypeLabel(doc.type)} – ${doc.numero}</p>
          <p><strong>Fournisseur :</strong> ${fournisseur?.raisonSociale}</p>
          <p><strong>Téléphone :</strong> ${fournisseur?.telephone || 'Non renseigné'}</p>
          <p style="margin-top:12px; color:#777; font-size:0.88em;">
            💡 En attendant, vous pouvez envoyer le PDF par email ou le télécharger pour l'envoyer manuellement.
          </p>
        </div>
      `,
      icon: 'info',
      confirmButtonText: '✅ Compris',
      confirmButtonColor: '#25D366',
      showCloseButton: true,
    });
  };

  // ── Frais supplémentaires (dans modal doc) ────────────────
  const addFrais = () => setDocFrais(prev => [...prev, { label: '', prix: 0 }]);
  const updateFrais = (i, field, val) => {
    const f = [...docFrais];
    f[i][field] = val;
    setDocFrais(f);
  };
  const removeFrais = (i) => setDocFrais(prev => prev.filter((_, idx) => idx !== i));
  const historiqueEntries = [
    ...documents.map(doc => ({
      key: `doc-${doc._id}`,
      type: 'document',
      date: new Date(doc.dateCreation),
      doc
    })),
    ...factures.flatMap(facture =>
      (facture.historique || []).map((log, index) => ({
        key: `fact-${facture._id}-${index}`,
        type: 'facture',
        date: new Date(log.date),
        facture,
        log
      }))
    ),
    ...historique.map(item => ({
      key: `hist-${item._id}`,
      type: 'manuel',
      date: new Date(item.createdAt),
      item
    }))
  ];

  const filteredHistoriqueEntries = historiqueEntries
    .filter(entry => historiqueFilter === 'factures' ? entry.type === 'facture' : true)
    .sort((a, b) => b.date - a.date);

  // ── Render ────────────────────────────────────────────────
  if (loading) return (
    <div className="detail-fournisseur-container">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="detail-fournisseur-content">
        <div className="df-loading-wrap"><span className="df-spinner"></span><p>Chargement...</p></div>
      </div>
    </div>
  );

  if (error || !fournisseur) return (
    <div className="detail-fournisseur-container">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="detail-fournisseur-content">
        <div className="error-message">{error || 'Fournisseur non trouvé'}</div>
        <button className="btn-back" onClick={() => onNavigate('fournisseurs')}>Retour à la liste</button>
      </div>
    </div>
  );

  if (docPageContext) return (
    <div className="detail-fournisseur-container">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="detail-fournisseur-content">
        <FournisseurDocumentFormPage
          fixedFournisseur={fournisseur}
          fixedFournisseurId={fournisseurId}
          produits={produitsFournisseur}
          documents={documents}
          initialType={docPageContext.initialType}
          initialDoc={docPageContext.initialDoc}
          initialProduitsQte={docPageContext.initialProduitsQte}
          sourceBonCommandeId={docPageContext.sourceBonCommandeId}
          mode={docPageContext.mode || 'create'}
          contextLabel="Detail fournisseur"
          refreshHistorique={fetchHistorique}
          onBack={() => setDocPageContext(null)}
          onCreated={async () => {
            await fetchDocumentsFournisseur();
            await fetchHistorique();
            setDocPageContext(null);
          }}
        />
      </div>
    </div>
  );

  if (selectedDetailDoc && !docPageContext) return (
    <div className="detail-fournisseur-container">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="detail-fournisseur-content">
        <FournisseurDocumentDetailPage
          doc={selectedDetailDoc}
          fournisseur={fournisseur}
          onBack={() => setSelectedDetailDoc(null)}
          onDownloadPDF={() => handleDownloadPDF(selectedDetailDoc._id, selectedDetailDoc.numero)}
          onSendEmail={() => openEmailModal(selectedDetailDoc)}
          onRappelEmail={() => {
            openEmailModal(selectedDetailDoc);
            setEmailObject(`RAPPEL – ${docTypeLabel(selectedDetailDoc.type)} N° ${selectedDetailDoc.numero}`);
          }}
          onWhatsApp={() => handleWhatsApp(selectedDetailDoc)}
          onConvertToBL={() => {
            const d = selectedDetailDoc;
            setSelectedDetailDoc(null);
            handleConvertToBonLivraison(d);
          }}
          onImportPalettes={() => openPaletteImportModal(selectedDetailDoc)}
          onDuplicate={() => {
            const d = selectedDetailDoc;
            setSelectedDetailDoc(null);
            handleDuplicateDocument(d);
          }}
          onEdit={() => {
            const d = selectedDetailDoc;
            setSelectedDetailDoc(null);
            handleEditDocument(d);
          }}
          onValidate={async () => {
            const d = selectedDetailDoc;
            await handleValidateDocument(d);
            setSelectedDetailDoc(null);
          }}
          onDelete={async () => {
            const d = selectedDetailDoc;
            await handleDeleteDocument(d._id, d.numero);
            setSelectedDetailDoc(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="detail-fournisseur-container">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="detail-fournisseur-content">

        {/* ── Header ── */}
        <div className="detail-header">
          <div className="header-left">
            <button className="btn-back" onClick={() => onNavigate('fournisseurs')}>
              <i className="fas fa-arrow-left"></i> Retour
            </button>
            <div>
              <h1>{fournisseur.raisonSociale}</h1>
              <p>{getTypeLabels(fournisseur)}</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-doc-dp" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => onNavigate('demandes-prix')}>
              <i className="fas fa-file-invoice-dollar"></i> Demandes de Prix
            </button>
            <button className="btn-doc-dp" style={{ background: '#10b981', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => setShowHistoryPriceModal(true)}>
              <i className="fas fa-chart-line"></i> Historique Prix Devis
            </button>
            {/* Nouveaux boutons BC / BL */}
            <button className="btn-doc-bc" onClick={() => openDocModal('bon_commande')}>
              <i className="fas fa-file-invoice"></i> Bon de Commande
            </button>
            <button className="btn-doc-bl df-btn-bl-locked" type="button" title="Creer un BL depuis un BC valide" onClick={() => openDocModal('bon_livraison')}>
              <i className="fas fa-lock"></i> BL depuis BC
            </button>
            <button className="btn-evaluation" onClick={() => setShowEvaluationModal(true)}>
              <i className="fas fa-star"></i> Évaluer
            </button>
            <button className="btn-add-historique" onClick={() => setShowHistoriqueModal(true)}>
              <i className="fas fa-plus"></i> Historique
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i> {message}
          </div>
        )}

        {/* ── Stats ── */}
        <div className="stats-grid">
          <div className="stat-card blue">
            <i className="fas fa-shopping-cart"></i>
            <div><h3>{fournisseur.nombreCommandes}</h3><p>Commandes</p></div>
          </div>
          <div className="stat-card green">
            <i className="fas fa-coins"></i>
            <div><h3>{formatCurrency(fournisseur.totalAchats)}</h3><p>Total Achats</p></div>
          </div>
          <div className="stat-card orange">
            <i className="fas fa-star"></i>
            <div><h3>{getEvaluationMoyenne()}/5</h3><p>Évaluation</p></div>
          </div>
          <div className="stat-card purple">
            <i className="fas fa-boxes"></i>
            <div><h3>{produitsFournisseur.length}</h3><p>Produits liés</p></div>
          </div>
        </div>

        {/* ── Détails ── */}
        <div className="details-grid">
          <div className="detail-card">
            <h3><i className="fas fa-info-circle"></i> Informations Générales</h3>
            <div className="info-row"><span className="label">Contact:</span><span className="value">{fournisseur.prenomContact} {fournisseur.nomContact}</span></div>
            <div className="info-row"><span className="label">Email:</span><span className="value">{fournisseur.email}</span></div>
            <div className="info-row"><span className="label">Téléphone:</span><span className="value">{fournisseur.telephone}</span></div>
            {fournisseur.telephoneSecondaire && (
              <div className="info-row"><span className="label">Tél. Secondaire:</span><span className="value">{fournisseur.telephoneSecondaire}</span></div>
            )}
            <div className="info-row">
              <span className="label">Statut:</span>
              <span className={`value statut-${fournisseur.statut.toLowerCase().replace(' ', '-')}`}>{fournisseur.statut}</span>
            </div>
          </div>
          <div className="detail-card">
            <h3><i className="fas fa-map-marker-alt"></i> Adresse</h3>
            <div className="info-row"><span className="label">Adresse:</span><span className="value">{fournisseur.adresse}</span></div>
            <div className="info-row"><span className="label">Ville:</span><span className="value">{fournisseur.ville}</span></div>
            {fournisseur.codePostal && <div className="info-row"><span className="label">Code Postal:</span><span className="value">{fournisseur.codePostal}</span></div>}
            <div className="info-row"><span className="label">Pays:</span><span className="value">{fournisseur.pays}</span></div>
          </div>
          <div className="detail-card">
            <h3><i className="fas fa-file-alt"></i> Informations Légales</h3>
            {fournisseur.matriculeFiscale && <div className="info-row"><span className="label">Matricule:</span><span className="value">{fournisseur.matriculeFiscale}</span></div>}
            {fournisseur.registreCommerce && <div className="info-row"><span className="label">RC:</span><span className="value">{fournisseur.registreCommerce}</span></div>}
            {fournisseur.siteWeb && (
              <div className="info-row"><span className="label">Site Web:</span>
                <span className="value"><a href={fournisseur.siteWeb} target="_blank" rel="noopener noreferrer">{fournisseur.siteWeb}</a></span>
              </div>
            )}
          </div>
          <div className="detail-card">
            <h3><i className="fas fa-cog"></i> Conditions Commerciales</h3>
            <div className="info-row"><span className="label">Paiement:</span><span className="value">{fournisseur.conditionsPaiement}</span></div>
            {fournisseur.delaiLivraison && <div className="info-row"><span className="label">Délai Livraison:</span><span className="value">{fournisseur.delaiLivraison}</span></div>}
          </div>
        </div>

        {/* ── Produits liés ── */}
        <div className="df-section">
          <div className="df-section-header">
            <h3><i className="fas fa-boxes"></i> Produits liés à ce fournisseur ({produitsFournisseur.length})</h3>
          </div>
          {produitsFournisseur.length === 0 ? (
            <p className="df-empty">Aucun produit associé à ce fournisseur pour l'instant.</p>
          ) : (
            <div className="df-produits-grid">
              {produitsFournisseur.map(p => (
                <div key={p._id} className="df-produit-chip">
                  <img src={`${API_URL.replace('/api', '')}${p.imageProduit}`} alt={p.nomProduit} onError={e => { e.target.style.display = 'none'; }} />
                  <div>
                    <strong>{p.nomProduit}</strong>
                    {p.reference && <span className="df-ref">Réf: {p.reference}</span>}
                    <span className="df-prix">{p.prix?.toLocaleString()} TND</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Documents émis pour ce fournisseur ── */}
        <div className="df-section">
          <div className="df-section-header">
            <h3><i className="fas fa-file-invoice"></i> Bons de Commande / Livraison ({documents.length})</h3>
          </div>
          {loadingDocs ? (
            <p className="df-empty">Chargement des documents...</p>
          ) : documents.length === 0 ? (
            <p className="df-empty">Aucun document émis pour ce fournisseur.<br />
              <button className="df-btn-inline" onClick={() => openDocModal('bon_commande')}>
                <i className="fas fa-plus"></i> Créer un bon de commande
              </button>
            </p>
          ) : (
            <div className="df-docs-list">
              {documents.map(doc => (
                <div key={doc._id} className="df-doc-card">
                  <div className="df-doc-left" style={{ cursor: 'pointer' }} onClick={() => setSelectedDetailDoc(doc)} title="Cliquer pour voir la fiche détaillée complète">
                    <span className={`df-doc-type-badge ${doc.type}`}>
                      {docTypeLabel(doc.type)}
                    </span>
                    <div>
                      <strong className="df-doc-numero">{doc.numero}</strong>
                      <span className="df-doc-date">{new Date(doc.dateCreation).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {doc.type !== 'bon_commande' && (
                      <div className="df-doc-montant">{(doc.montantTTC || doc.montant)?.toLocaleString()} TND</div>
                    )}
                    <span className={`df-doc-statut statut-${doc.statut}`}>{doc.statut}</span>
                    {doc.archiveFournisseur && (
                      <span className="df-doc-statut" style={{ background: '#e2e8f0', color: '#334155' }}>archive</span>
                    )}
                    {doc.termineFournisseur && (
                      <span className="df-doc-statut" style={{ background: '#dcfce7', color: '#166534' }}>termine</span>
                    )}
                  </div>
                  <div className="df-doc-actions">
                    {/* Voir détails */}
                    <button className="df-action-btn" title="Voir la fiche détaillée complète" onClick={() => setSelectedDetailDoc(doc)} style={{ background: '#1e3a5f', color: 'white' }}>
                      <i className="fas fa-eye"></i>
                      <span>Voir détails</span>
                    </button>
                    {/* Télécharger PDF */}
                    <button className="df-action-btn df-btn-pdf" title="Télécharger PDF" onClick={() => handleDownloadPDF(doc._id, doc.numero)}>
                      <i className="fas fa-file-pdf"></i>
                      <span>PDF</span>
                    </button>
                    {/* Email */}
                    <button className="df-action-btn df-btn-email" title="Envoyer par email" onClick={() => openEmailModal(doc)}>
                      <i className="fas fa-envelope"></i>
                      <span>Email</span>
                    </button>
                    {/* Rappel Email */}
                    <button className="df-action-btn df-btn-rappel" title="Rappel par email" onClick={() => {
                      openEmailModal(doc);
                      setEmailObject(`RAPPEL – ${docTypeLabel(doc.type)} N° ${doc.numero}`);
                    }}>
                      <i className="fas fa-bell"></i>
                      <span>Rappel</span>
                    </button>
                    {/* WhatsApp */}
                    <button className="df-action-btn df-btn-whatsapp" title="Envoyer via WhatsApp" onClick={() => handleWhatsApp(doc)}>
                      <i className="fab fa-whatsapp"></i>
                      <span>WhatsApp</span>
                    </button>
                    {doc.type === 'bon_commande' && doc.statut === 'valide' && (
                      <button className="df-action-btn" title="Convertir en BL" onClick={() => handleConvertToBonLivraison(doc)} style={{ background: '#f59e0b', color: 'white' }}>
                        <i className="fas fa-truck"></i>
                        <span>Convertir en BL</span>
                      </button>
                    )}
                    {doc.type === 'bon_livraison' && doc.statut === 'valide' && (
                      <button className="df-action-btn df-btn-import-palettes" title="Importer palettes Excel" onClick={() => openPaletteImportModal(doc)}>
                        <i className="fas fa-file-excel"></i>
                        <span>Palettes</span>
                      </button>
                    )}
                    {doc.type === 'bon_commande' && (
                      <button className="df-action-btn" title="Dupliquer" onClick={() => handleDuplicateDocument(doc)} style={{ background: '#2563eb', color: 'white' }}>
                        <i className="fas fa-copy"></i>
                        <span>Dupliquer</span>
                      </button>
                    )}
                    {canEditOrDeleteDocument(doc) && (
                      <>
                        <button className="df-action-btn" title="Modifier ce document" onClick={() => handleEditDocument(doc)} style={{ background: '#0f766e', color: 'white' }}>
                          <i className="fas fa-edit"></i>
                          <span>Modifier</span>
                        </button>
                        <button className="df-action-btn df-btn-delete" title="Supprimer ce document" onClick={() => handleDeleteDocument(doc._id, doc.numero)} style={{ background: '#fff', color: '#dc3545', border: '1px solid #dc3545' }}>
                          <i className="fas fa-trash-alt"></i>
                          <span>Supprimer</span>
                        </button>
                      </>
                    )}
                    {doc.statut !== 'valide' && (
                      <button className="df-action-btn" title="Valider ce document" onClick={() => handleValidateDocument(doc)} style={{ background: '#16a34a', color: 'white' }}>
                        <i className="fas fa-check-circle"></i>
                        <span>Valider</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Factures fournisseur ── */}
        <div className="df-section">
          <div className="df-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <h3><i className="fas fa-receipt"></i> Factures fournisseur ({factures.length})</h3>
            {!showFactureForm && (
              <button className="btn-doc-bc" onClick={openFactureForm}>
                <i className="fas fa-plus"></i> Ajouter facture
              </button>
            )}
            {showFactureForm && (
              <button onClick={closeFactureForm} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', padding: '0.5rem 1.1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="fas fa-times"></i> Annuler
              </button>
            )}
          </div>

          {/* ── Formulaire inline ── */}
          {showFactureForm && (
            <form onSubmit={handleCreateFacture} style={{ marginTop: '16px', background: 'linear-gradient(135deg, #f0f7ff 0%, #fafbff 100%)', border: '2px solid #3b82f6', borderRadius: '16px', padding: '2rem', animation: 'fadeIn 0.3s ease' }}>

              {/* Titre section */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                <div style={{ width: '42px', height: '42px', background: '#3b82f6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-receipt" style={{ color: 'white', fontSize: '1.1rem' }}></i>
                </div>
                <div>
                  <h4 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.1rem', fontWeight: '800' }}>Nouvelle Facture Fournisseur</h4>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.87rem' }}>Renseignez les informations de la facture reçue du fournisseur</p>
                </div>
              </div>

              {/* Grille principale 2 colonnes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>

                {/* ── BLOC 1 : Identification ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-id-card" style={{ color: '#3b82f6' }}></i> Identification
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>N° Facture *</label>
                      <input type="text" name="numeroFacture" value={factureForm.numeroFacture} onChange={handleFactureChange} required placeholder="Ex : FAC-2024-001" style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', transition: 'border 0.2s' }} onFocus={e => e.target.style.borderColor='#3b82f6'} onBlur={e => e.target.style.borderColor='#e2e8f0'} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Date facture</label>
                      <input type="date" name="dateFacture" value={factureForm.dateFacture} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Date réception</label>
                      <input type="date" name="dateReception" value={factureForm.dateReception} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                </div>

                {/* ── BLOC 2 : Bons de livraison ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-truck" style={{ color: '#f59e0b' }}></i> Bons de Livraison *
                  </h5>
                  {bonsLivraisonOptions.length === 0 ? (
                    <div style={{ padding: '20px', background: '#fef9c3', borderRadius: '8px', color: '#92400e', fontSize: '0.875rem', textAlign: 'center' }}>
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                      Aucun bon de livraison validé disponible
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                      {bonsLivraisonOptions.map(bl => (
                        <label key={bl._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', border: factureForm.bonsLivraison.includes(bl._id) ? '2px solid #3b82f6' : '1px solid #e2e8f0', background: factureForm.bonsLivraison.includes(bl._id) ? '#eff6ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }}>
                          <input type="checkbox" checked={factureForm.bonsLivraison.includes(bl._id)} onChange={e => handleSelectBonLivraison(bl._id, e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '700', color: '#1e3a5f', fontSize: '0.9rem' }}>{bl.numero}</div>
                            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{(bl.montantTTC || bl.montant || 0).toLocaleString()} TND</div>
                          </div>
                          {factureForm.bonsLivraison.includes(bl._id) && <i className="fas fa-check-circle" style={{ color: '#3b82f6' }}></i>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── BLOC 2.5 : Taxes Additionnelles ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', gridColumn: '1 / -1' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-file-invoice-dollar" style={{ color: '#ec4899' }}></i> Taxes Additionnelles
                  </h5>
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <input 
                        type="checkbox" 
                        name="fodecApplicable"
                        checked={factureForm.fodecApplicable} 
                        onChange={handleFactureChange} 
                        style={{ width: '18px', height: '18px', accentColor: '#ec4899' }}
                      />
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>Appliquer FODEC (1 %)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <input 
                        type="checkbox" 
                        name="timbreFiscalApplicable"
                        checked={factureForm.timbreFiscalApplicable} 
                        onChange={handleFactureChange} 
                        style={{ width: '18px', height: '18px', accentColor: '#ec4899' }}
                      />
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>Appliquer Timbre fiscal (1,000 DT)</span>
                    </label>
                  </div>
                </div>

                {/* ── BLOC 3 : Montants ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-coins" style={{ color: '#10b981' }}></i> Montants
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '500', textTransform: 'none', letterSpacing: 0 }}>(modifiables par la finance)</span>
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Montant HT (TND)</label>
                      <input type="number" step="0.001" name="montantHT" value={factureForm.montantHT} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>TVA (TND)</label>
                      <input type="number" step="0.001" name="tva" value={factureForm.tva} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Montant TTC (TND)</label>
                      <input type="number" step="0.001" name="montantTTC" value={factureForm.montantTTC} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #10b981', borderRadius: '8px', fontSize: '1.05rem', fontWeight: '700', outline: 'none', boxSizing: 'border-box', background: '#f0fdf4' }} />
                    </div>
                  </div>
                </div>

                {/* ── BLOC 4 : Acompte ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-hand-holding-usd" style={{ color: '#8b5cf6' }}></i> Acompte
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Montant acompte (TND)</label>
                      <input type="number" step="0.001" name="acompteMontant" value={factureForm.acompteMontant} onChange={handleFactureChange} placeholder="0.000" style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    {Number(factureForm.acompteMontant) > 0 && (
                      <>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Date acompte *</label>
                          <input type="date" name="acompteDate" value={factureForm.acompteDate} onChange={handleFactureChange} required style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #8b5cf6', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Méthode acompte</label>
                          <select name="acompteMethode" value={factureForm.acompteMethode} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                            <option value="traite">Traïte</option>
                            <option value="cheque">Chèque</option>
                            <option value="virement">Virement</option>
                            <option value="versement_bancaire">Versement bancaire</option>
                            <option value="especes">Espèces</option>
                            <option value="autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Référence acompte</label>
                          <input type="text" name="acompteReference" value={factureForm.acompteReference} onChange={handleFactureChange} placeholder="N° chèque, virement..." style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        {factureForm.acompteMethode === 'especes' && (
                          <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Nom du recepteur</label>
                            <input type="text" name="acompteNomRecepteur" value={factureForm.acompteNomRecepteur} onChange={handleFactureChange} placeholder="Nom et prénom" style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── BLOC 5 : Mode de règlement ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', gridColumn: 'span 1' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-calendar-alt" style={{ color: '#f59e0b' }}></i> Règlement
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Mode de paiement du reste</label>
                      <select name="modePaiementReste" value={factureForm.modePaiementReste} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                        <option value="comptant">Comptant</option>
                        <option value="facilite">Paiement facilisé (échéances)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Type de règlement</label>
                      <select name="typeReglement" value={factureForm.typeReglement} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                        <option value="traite">Traïte</option>
                        <option value="cheque">Chèque</option>
                        <option value="virement">Virement</option>
                        <option value="versement_bancaire">Versement bancaire</option>
                        <option value="especes">Espèces</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    {factureForm.modePaiementReste === 'facilite' ? (
                      <>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Nombre d'échéances</label>
                          <input type="number" min="1" name="nbEcheances" value={factureForm.nbEcheances} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Périodicité (mois)</label>
                          <input type="number" min="1" name="periodiciteMois" value={factureForm.periodiciteMois} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Date première échéance</label>
                          <input type="date" name="datePremiereEcheance" value={factureForm.datePremiereEcheance} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      </>
                    ) : (
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Date paiement comptant</label>
                        <input type="date" name="datePremiereEcheance" value={factureForm.datePremiereEcheance} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── BLOC Remise 2 ── */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h5 style={{ margin: '0 0 16px 0', color: '#1e3a5f', fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-tags" style={{ color: '#ec4899' }}></i> 2ᵉ Remise Facture
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Application 2ᵉ remise</label>
                      <select name="remise2Mode" value={factureForm.remise2Mode} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                        <option value="aucun">Aucune (Pas de 2ᵉ remise)</option>
                        <option value="produit">Par produit (Héritée des BLs)</option>
                        <option value="global">Sur le total de la facture</option>
                      </select>
                    </div>
                    {factureForm.remise2Mode === 'global' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Valeur remise 2</label>
                          <input type="number" min="0" step="0.001" name="remise2Val" value={factureForm.remise2Val} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' }}>Type</label>
                          <select name="remise2Type" value={factureForm.remise2Type} onChange={handleFactureChange} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}>
                            <option value="pourcentage">Pourcentage (%)</option>
                            <option value="montant">Montant fixe (DT)</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>{/* fin grille */}

              {/* Aperçu montants si BL sélectionnés */}
              {factureForm.bonsLivraison.length > 0 && (
                <div style={{ marginTop: '20px', padding: '16px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <i className="fas fa-info-circle" style={{ color: '#3b82f6', fontSize: '1.2rem' }}></i>
                  <span style={{ color: '#1d4ed8', fontSize: '0.875rem', fontWeight: '600' }}>
                    {factureForm.bonsLivraison.length} BL sélectionné(s) — Montant auto-calculé : <strong>{formatCurrency(factureForm.montantTTC)}</strong> TTC
                  </span>
                  <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Vous pouvez ajuster les montants ci-dessus selon la facture réelle.</span>
                </div>
              )}

              {/* Boutons d'action */}
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <button type="button" onClick={closeFactureForm} style={{ padding: '11px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-times"></i> Annuler
                </button>
                <button type="submit" disabled={submittingFacture} style={{ padding: '11px 28px', background: submittingFacture ? '#94a3b8' : 'linear-gradient(135deg, #1e3a5f, #3b82f6)', border: 'none', color: 'white', borderRadius: '10px', cursor: submittingFacture ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(59,130,246,0.35)' }}>
                  <i className={`fas ${submittingFacture ? 'fa-spinner fa-spin' : 'fa-check'}`}></i> {submittingFacture ? 'Enregistrement...' : `${factureForm.isEditingFacture ? 'Modifier' : 'Enregistrer'} la facture`}
                </button>
              </div>

            </form>
          )}

          {/* ── Liste des factures ── */}
          {loadingFactures ? (
            <p className="df-empty">Chargement des factures...</p>
          ) : factures.length === 0 ? (
            <p className="df-empty">Aucune facture fournisseur enregistrée.</p>
          ) : (
            <div className="df-docs-list">
              {factures.map(facture => (
                <div key={facture._id} className="df-doc-card">
                  <div className="df-doc-left" style={{ flexWrap: 'wrap', gap: '10px' }}>
                    <span className="df-doc-type-badge">Facture</span>
                    <div>
                      <strong className="df-doc-numero">{facture.numeroFacture}</strong>
                      <span className="df-doc-date">{new Date(facture.dateFacture).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="df-doc-montant">{(facture.montantTTC || 0).toLocaleString()} TND</div>
                    <span className={`df-doc-statut statut-${facture.statut}`}>{facture.statut}</span>
                    {facture.fodecApplicable && (
                      <span style={{ background: '#fce7f3', color: '#be185d', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>FODEC</span>
                    )}
                    {facture.timbreFiscalApplicable && (
                      <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>Timbre Fiscal</span>
                    )}
                  </div>
                  <div style={{ width: '100%', marginTop: '10px', color: '#475569' }}>
                    <strong>Bons de livraison:</strong>{' '}
                    {Array.isArray(facture.bonsLivraison) && facture.bonsLivraison.length > 0
                      ? facture.bonsLivraison.map(bl => bl.numero || bl).join(', ')
                      : '—'}
                  </div>

                  {/* Résumé financier */}
                  {(() => {
                    const summary = getFinancialSummary(facture);
                    return (
                      <div style={{ width: '100%', marginTop: '8px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem' }}>
                          <i className="fas fa-check-circle" style={{ marginRight: '5px' }}></i>
                          Payé : {formatCurrency(summary.totalPaye)}
                        </span>
                        <span style={{ background: summary.resteARegler > 0 ? '#fef9c3' : '#dcfce7', color: summary.resteARegler > 0 ? '#92400e' : '#166534', padding: '4px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem' }}>
                          <i className="fas fa-clock" style={{ marginRight: '5px' }}></i>
                          Reste : {formatCurrency(summary.resteARegler)}
                        </span>
                        {summary.statutPaiement && (
                          <span style={{ background: summary.statutPaiement === 'payee' ? '#dcfce7' : summary.statutPaiement === 'en_retard' ? '#fee2e2' : '#dbeafe', color: summary.statutPaiement === 'payee' ? '#166534' : summary.statutPaiement === 'en_retard' ? '#991b1b' : '#1e40af', padding: '4px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem' }}>
                            {summary.statutPaiement === 'payee' ? 'Payee' : summary.statutPaiement === 'partiellement_payee' ? 'Partiellement payee' : summary.statutPaiement === 'en_retard' ? 'En retard' : 'A payer'}
                          </span>
                        )}
                        {facture.verification?.status === 'warning' && (
                          <span title={facture.verification.details?.join('\n')} style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '8px', fontSize: '0.85rem', cursor: 'help' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                            Écarts détectés
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ width: '100%', marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                    {facture.statut !== 'annule' && (
                      <button className="df-action-btn" onClick={() => openEditFactureForm(facture)} style={{ background: '#0f766e', color: 'white' }}>
                        <i className="fas fa-edit"></i>
                        <span>Modifier</span>
                      </button>
                    )}
                    {facture.statut !== 'archive' && (
                      <button className="df-action-btn" onClick={() => handleArchiveFacture(facture)} style={{ background: '#16a34a', color: 'white' }}>
                        <i className="fas fa-archive"></i>
                        <span>Archiver</span>
                      </button>
                    )}
                    {facture.statut !== 'annule' && (
                      <button className="df-action-btn" onClick={() => handleAnnulerFacture(facture)} style={{ background: '#dc2626', color: 'white' }}>
                        <i className="fas fa-ban"></i>
                        <span>Annuler</span>
                      </button>
                    )}
                  </div>
                  <div style={{ width: '100%', marginTop: '12px', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <strong style={{ color: '#1e293b' }}>Échéances</strong>
                    {Array.isArray(facture.echeances) && facture.echeances.length > 0 ? (
                      <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {facture.echeances.map(ech => {
                          const retard = isEcheanceEnRetard(ech) || ech.statut === 'en_retard';
                          const statutDisplay = retard ? 'en_retard' : ech.statut;
                          const rowBg = retard ? '#fef2f2' : '#fff';
                          const borderColor = retard ? '#fecaca' : '#e5e7eb';
                          return (
                          <div key={ech._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', background: rowBg, border: `1px solid ${borderColor}`, padding: '10px 12px', borderRadius: '8px', transition: 'all 0.2s' }}>
                            <div>
                              <strong>{new Date(ech.dateEcheance).toLocaleDateString('fr-FR')}</strong>
                              <span style={{ marginLeft: '10px', color: '#64748b' }}>{ech.methode}</span>
                              {retard && <span style={{ marginLeft: '8px', color: '#dc2626', fontSize: '0.8rem', fontWeight: '700' }}>⏰ EN RETARD</span>}
                            </div>
                            <div style={{ fontWeight: '700', color: '#0f172a' }}>{(ech.montant || 0).toLocaleString()} TND</div>
                            <span className={`df-doc-statut statut-${statutDisplay}`} style={{ minWidth: '90px', textAlign: 'center' }}>{statutDisplay}</span>
                            {ech.statut !== 'payee' && (
                              <>
                                <button className="df-action-btn" onClick={() => openModifierEcheanceModal(facture, ech)} style={{ background: '#f59e0b', color: 'white' }}>
                                  <i className="fas fa-edit"></i>
                                  <span>Modifier</span>
                                </button>
                                <button className="df-action-btn" onClick={() => openEcheanceModal(facture, ech)} style={{ background: '#16a34a', color: 'white' }}>
                                  <i className="fas fa-cash-register"></i>
                                  <span>Marquer payé</span>
                                </button>
                              </>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="df-empty" style={{ marginTop: '8px' }}>Aucune échéance planifiée.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Évaluation ── */}
        <div className="evaluation-section">
          <h3><i className="fas fa-chart-bar"></i> Évaluation Détaillée</h3>
          <div className="evaluation-grid">
            {[['qualite','Qualité'], ['prix','Prix'], ['delai','Délai'], ['service','Service']].map(([key, label]) => (
              <div key={key} className="evaluation-item">
                <span>{label}</span>
                <div className="rating-bar"><div className="rating-fill" style={{ width: `${(fournisseur.evaluation[key] / 5) * 100}%` }}></div></div>
                <span>{fournisseur.evaluation[key]}/5</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Historique enrichi (manuel + BC/BL) ── */}
        <div className="historique-section">
          <h3>
            <i className="fas fa-history"></i>
            Historique ({filteredHistoriqueEntries.length} entrées)
          </h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              className={`df-type-btn ${historiqueFilter === 'all' ? 'active' : ''}`}
              type="button"
              onClick={() => setHistoriqueFilter('all')}
            >
              Tous
            </button>
            <button
              className={`df-type-btn ${historiqueFilter === 'factures' ? 'active' : ''}`}
              type="button"
              onClick={() => setHistoriqueFilter('factures')}
            >
              Factures
            </button>
          </div>
          <div className="historique-timeline">
            {filteredHistoriqueEntries.map(entry => {
              if (entry.type === 'document') {
                const doc = entry.doc;
                return (
                  <div key={entry.key} className="timeline-item">
                    <div className="timeline-icon" style={{ backgroundColor: doc.type === 'bon_commande' ? '#1e3a5f' : '#f59e0b' }}>
                      <i className={`fas ${doc.type === 'bon_commande' ? 'fa-file-invoice' : 'fa-truck'}`}></i>
                    </div>
                    <div className="timeline-content timeline-doc-card">
                      <div className="timeline-header">
                        <div className="timeline-doc-title">
                          <span className={`df-doc-type-badge ${doc.type}`}>{docTypeLabel(doc.type)}</span>
                          <h4>{doc.numero}</h4>
                        </div>
                        <span className="timeline-date">{new Date(doc.dateCreation).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {doc.description && <p className="timeline-description">{doc.description}</p>}
                      <div className="timeline-footer timeline-doc-footer">
                        {doc.type !== 'bon_commande' && (
                          <span className="timeline-montant">{(doc.montantTTC || doc.montant || 0).toLocaleString()} TND</span>
                        )}
                        <span className={`timeline-statut statut-${doc.statut}`}>{doc.statut}</span>
                        {/* Boutons d'action */}
                        <div className="df-doc-actions">
                          {doc.type === 'bon_commande' && doc.statut === 'valide' && (
                            <button className="df-action-btn" title="Convertir en BL"
                              onClick={() => handleConvertToBonLivraison(doc)}
                              style={{ backgroundColor: '#f59e0b', color: 'white' }}>
                              <i className="fas fa-truck"></i><span>Convertir</span>
                            </button>
                          )}
                          {doc.type === 'bon_commande' && (
                             <button className="df-action-btn" title="Dupliquer"
                               onClick={() => handleDuplicateDocument(doc)}
                               style={{ backgroundColor: '#2563eb', color: 'white' }}>
                               <i className="fas fa-copy"></i><span>Dupliquer</span>
                             </button>
                          )}
                          {canEditOrDeleteDocument(doc) && (
                            <button className="df-action-btn" title="Modifier ce document"
                              onClick={() => handleEditDocument(doc)}
                              style={{ backgroundColor: '#0f766e', color: 'white' }}>
                              <i className="fas fa-edit"></i><span>Modifier</span>
                            </button>
                          )}
                          <button className="df-action-btn df-btn-pdf" title="Télécharger PDF"
                            onClick={() => handleDownloadPDF(doc._id, doc.numero)}>
                            <i className="fas fa-file-pdf"></i><span>PDF</span>
                          </button>
                          <button className="df-action-btn df-btn-email" title="Envoyer par email"
                            onClick={() => openEmailModal(doc)}>
                            <i className="fas fa-envelope"></i><span>Email</span>
                          </button>
                          <button className="df-action-btn df-btn-rappel" title="Rappel par email" onClick={() => openEmailModal(doc, true)}>
                            <i className="fas fa-bell"></i><span>Rappel</span>
                          </button>
                          <button className="df-action-btn df-btn-whatsapp" title="Envoyer via WhatsApp"
                            onClick={() => handleWhatsApp(doc)}>
                            <i className="fab fa-whatsapp"></i><span>WhatsApp</span>
                          </button>

                          {canEditOrDeleteDocument(doc) && (
                            <button className="df-action-btn df-btn-delete" title="Supprimer ce document"
                              onClick={() => handleDeleteDocument(doc._id, doc.numero)}
                              style={{ backgroundColor: '#fff', color: '#dc3545', border: '1px solid #dc3545', marginLeft: 'auto' }}>
                              <i className="fas fa-trash-alt"></i><span>Supprimer</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (entry.type === 'facture') {
                const facture = entry.facture;
                const log = entry.log;
                return (
                  <div key={entry.key} className="timeline-item">
                    <div className="timeline-icon" style={{ backgroundColor: '#0ea5e9' }}>
                      <i className="fas fa-receipt"></i>
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <h4>Facture {facture.numeroFacture} — {log.action}</h4>
                        <span className="timeline-date">{formatDate(log.date)}</span>
                      </div>
                      <p className="timeline-description">{log.commentaire || 'Action sur facture fournisseur'}</p>
                      <div className="timeline-footer">
                        <span className="timeline-type">Facture fournisseur</span>
                        <span className="timeline-montant">{formatCurrency(facture.montantTTC)}</span>
                        <span className={`timeline-statut statut-${facture.statut}`}>{facture.statut}</span>
                      </div>
                    </div>
                  </div>
                );
              }

              const item = entry.item;
              return (
                <div key={entry.key} className="timeline-item">
                  <div className="timeline-icon" style={{ backgroundColor: getTypeColor(item.type) }}>
                    <i className={`fas fa-${getTypeIcon(item.type)}`}></i>
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h4>{item.titre}</h4>
                      <span className="timeline-date">{formatDate(item.createdAt)}</span>
                    </div>
                    <p className="timeline-description">{item.description}</p>
                    <div className="timeline-footer">
                      <span className="timeline-type">{item.type}</span>
                      {item.montant > 0 && <span className="timeline-montant">{formatCurrency(item.montant)}</span>}
                      <span className={`timeline-statut statut-${item.statut?.toLowerCase().replace(' ', '-')}`}>{item.statut}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredHistoriqueEntries.length === 0 && (
              <p className="no-historique">Aucune entrée dans l'historique</p>
            )}
          </div>
        </div>

        {/* ══════ MODALE : Créer BC / BL ══════ */}
        {showPaletteImportModal && paletteImportDoc && (
          <div className="modal-overlay" onClick={closePaletteImportModal}>
            <div className="modal-content df-modal-large df-palette-import-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <i className="fas fa-file-excel"></i>&nbsp;
                  Importer palettes - {paletteImportDoc.numero}
                </h2>
                <button className="modal-close" onClick={closePaletteImportModal} disabled={paletteImporting}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body df-modal-body">
                <div className="df-import-warning">
                  <i className="fas fa-info-circle"></i>
                  <div>
                    <strong>Le stock produit ne sera pas incremente ici.</strong>
                    <span>La reception est deja comptabilisee par la validation du BL fournisseur.</span>
                  </div>
                </div>

                <label className="df-palette-upload">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handlePaletteImportFileChange}
                    disabled={paletteImporting}
                  />
                  <div>
                    <i className="fas fa-cloud-upload-alt"></i>
                    <strong>{paletteImportFileName || 'Selectionner un fichier Excel'}</strong>
                    <span>Colonnes attendues: Referenz, Palette, Seriennummer, Artikel, Beschreibung</span>
                  </div>
                </label>

                {paletteImportPreview.length > 0 && (
                  <div className="df-import-preview">
                    <h3>Apercu des donnees</h3>
                    <div className="df-import-table-wrap">
                      <table className="df-import-table">
                        <thead>
                          <tr>
                            <th>Referenz</th>
                            <th>Palette</th>
                            <th>Seriennummer</th>
                            <th>Artikel</th>
                            <th>Beschreibung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paletteImportPreview.map((row, index) => (
                            <tr key={index}>
                              <td>{row.Referenz || row.Reference || row.reference || '-'}</td>
                              <td>{row.Palette || row.palette || '-'}</td>
                              <td>{row.Seriennummer || row.NumeroSerie || row.numeroSerie || '-'}</td>
                              <td>{row.Artikel || row.Article || row.article || '-'}</td>
                              <td>{row.Beschreibung || row.ReferenceProduit || row.referenceProduit || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closePaletteImportModal} disabled={paletteImporting}>
                  Annuler
                </button>
                <button type="button" className="btn-submit" onClick={handleImportPalettesBL} disabled={!paletteImportFile || paletteImporting}>
                  {paletteImporting ? 'Import en cours...' : 'Importer les palettes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {false && showDocModal && (
          <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
            <div className="modal-content df-modal-large" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <i className={`fas ${docType === 'bon_commande' ? 'fa-file-invoice' : 'fa-truck'}`}></i>&nbsp;
                  Créer un {docTypeLabel(docType)}
                </h2>
                <button className="modal-close" onClick={() => setShowDocModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleCreateDocument}>
                <div className="modal-body df-modal-body">

                  {/* Type switcher */}
                  <div className="df-type-switcher">
                    <button type="button" className={`df-type-btn ${docType === 'bon_commande' ? 'active' : ''}`} disabled={docType === 'bon_livraison'} onClick={() => { setDocType('bon_commande'); setConversionBonCommandeId(null); }}>
                      <i className="fas fa-file-invoice"></i> Bon de Commande
                    </button>
                    <button type="button" className={`df-type-btn ${docType === 'bon_livraison' ? 'active' : ''}`} disabled={!conversionBonCommandeId} onClick={() => conversionBonCommandeId && setDocType('bon_livraison')}>
                      <i className={`fas ${conversionBonCommandeId ? 'fa-truck' : 'fa-lock'}`}></i> Bon de Livraison
                    </button>
                  </div>

                  <div className="df-doc-context-card">
                    <div>
                      <span>Fournisseur</span>
                      <strong>{fournisseur?.raisonSociale || '-'}</strong>
                      <small>{fournisseur?.email || 'Email non renseigne'}</small>
                    </div>
                    <div>
                      <span>Workflow</span>
                      <strong>{docType === 'bon_livraison' ? 'BC vers BL' : 'Commande fournisseur'}</strong>
                      <small>
                        {docType === 'bon_livraison'
                          ? `Source: ${documents.find(d => d._id === conversionBonCommandeId)?.numero || 'BC selectionne'}`
                          : 'Le BL sera cree depuis le BC valide'}
                      </small>
                    </div>
                  </div>

                  {docType === 'bon_commande' && (
                    <label className="df-doc-email-option">
                      <input
                        type="checkbox"
                        checked={sendBonCommandeEmail}
                        onChange={e => setSendBonCommandeEmail(e.target.checked)}
                      />
                      <span>
                        <strong>Envoyer le bon de commande par email au fournisseur</strong>
                        <small>Option desactivee par defaut. Le document sera cree sans email si elle reste decochee.</small>
                      </span>
                    </label>
                  )}

                  {/* Sélection produits avec quantités */}
                  <div className="form-group">
                    <label>
                      <i className="fas fa-boxes"></i> Sélectionner les produits *
                      <span className="df-hint"> ({nbSelectionnes} sélectionné(s))</span>
                    </label>
                    {produitsFournisseur.length === 0 ? (
                      <p className="df-warn">⚠️ Aucun produit n'est encore lié à ce fournisseur.</p>
                    ) : (
                      <div className="df-produits-select">
                        {produitsFournisseur.map(p => {
                          const lineConfig = produitsQte[p._id] || null;
                          const isSelected = produitsQte[p._id] !== undefined;
                          const isLineaire = isProduitLineaire(p);
                          const qte = lineConfig && lineConfig.qte ? lineConfig.qte : 1;
                          const decoupes = isLineaire
                            ? recalculateLineaireEntry(lineConfig || buildDefaultSelection(p)).decoupes
                            : [];
                          const metres = isLineaire
                            ? Number(decoupes.reduce((sum, d) => sum + d.metres, 0).toFixed(3))
                            : 0;
                          const prixUnitaire = getPrixUnitaireAchat(p);

                          return (
                            <div key={p._id} className={`df-produit-check ${isSelected ? 'selected' : ''}`} style={{ transition: 'all 0.2s', border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0', background: isSelected ? '#f0f7ff' : '#fff', borderRadius: '8px', marginBottom: '10px', padding: '12px' }}>
                              {/* Checkbox + infos */}
                              <label className="df-produit-check-label" onClick={(e) => { e.preventDefault(); handleToggleProduit(p); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', width: '100%', margin: 0 }}>
                                <input type="checkbox" checked={isSelected} readOnly style={{ transform: 'scale(1.2)' }} />
                                <div className="df-produit-check-info" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                  <strong style={{ color: isSelected ? '#1e3a5f' : '#334155', fontSize: '1.05rem' }}>{p.nomProduit}</strong>
                                  {p.reference && <span className="df-ref">Réf: {p.reference}</span>}
                                  <span className="df-prix" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                                    <span style={{ color: '#059669', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{prixUnitaire.toLocaleString()} TND / {isLineaire ? 'm' : 'unité'}</span>
                                    <span style={{ color: '#94a3b8' }}>·</span>
                                    <span className="df-stock-badge" style={{ color: '#475569', fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block' }}>Stock: {formatStockQuantity(p.quantite, isLineaire)}</span>
                                  </span>
                                </div>
                              </label>
                              {/* Champ quantité — visible seulement si sélectionné */}
                              {isSelected && (
                                <div className="df-qte-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '12px', borderTop: '1px dashed #cbd5e1', paddingTop: '12px' }}>
                                  {isLineaire ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                        {decoupes.map((decoupe, decoupeIndex) => (
                                          <div key={`${p._id}-decoupe-${decoupeIndex}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                            <span style={{ color: '#475569', fontWeight: '600' }}>Pièces</span>
                                            <input
                                              type="number"
                                              className="df-qte-input"
                                              min="1"
                                              value={decoupe.nombrePieces}
                                              onChange={e => handleSetLineaireDecoupe(p._id, decoupeIndex, 'nombrePieces', e.target.value)}
                                              onClick={e => e.stopPropagation()}
                                              style={{ width: '80px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: 'bold', outline: 'none', padding: '6px' }}
                                            />
                                            <span style={{ color: '#475569', fontWeight: '600' }}>Longueur/p</span>
                                            <input
                                              type="number"
                                              className="df-qte-input"
                                              min="0.001"
                                              step="0.001"
                                              value={decoupe.longueurPieceMetre}
                                              onChange={e => handleSetLineaireDecoupe(p._id, decoupeIndex, 'longueurPieceMetre', e.target.value)}
                                              onClick={e => e.stopPropagation()}
                                              style={{ width: '100px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: 'bold', outline: 'none', padding: '6px' }}
                                            />
                                            <span style={{ color: '#475569' }}>m</span>
                                            {decoupes.length > 1 && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveLineaireDecoupe(p._id, decoupeIndex);
                                                }}
                                                style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontWeight: 'bold' }}
                                              >
                                                -
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAddLineaireDecoupe(p._id);
                                            }}
                                            style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontWeight: 'bold' }}
                                          >
                                            + Découpe
                                          </button>
                                        </div>
                                      </div>
                                      <span className="df-qte-total" style={{ marginLeft: '8px', fontWeight: 'bold', color: '#1e293b' }}>
                                        = {metres.toLocaleString()} m ({(prixUnitaire * metres).toLocaleString()} TND)
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden' }}>
                                        <button type="button" className="df-qte-btn" onClick={() => handleSetQte(p._id, qte - 1)} style={{ padding: '6px 14px', background: '#f1f5f9', border: 'none', borderRight: '1px solid #cbd5e1', cursor: 'pointer', color: '#3b82f6', fontWeight: 'bold' }}>−</button>
                                        <input
                                          type="number"
                                          className="df-qte-input"
                                          min="1"
                                          value={qte}
                                          onChange={e => handleSetQte(p._id, e.target.value)}
                                          onClick={e => e.stopPropagation()}
                                          style={{ width: '60px', textAlign: 'center', border: 'none', fontWeight: 'bold', outline: 'none' }}
                                        />
                                        <button type="button" className="df-qte-btn" onClick={() => handleSetQte(p._id, qte + 1)} style={{ padding: '6px 14px', background: '#f1f5f9', border: 'none', borderLeft: '1px solid #cbd5e1', cursor: 'pointer', color: '#3b82f6', fontWeight: 'bold' }}>+</button>
                                      </div>
                                      <span className="df-qte-total" style={{ marginLeft: '15px', fontWeight: 'bold', color: '#1e293b' }}>
                                        = {(prixUnitaire * qte).toLocaleString()} TND
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Sous-total */}
                    {nbSelectionnes > 0 && (
                      <div className="df-subtotal">
                        <span>Total produits HT :</span>
                        <strong>
                          {Object.entries(produitsQte).reduce((s, [id, line]) => {
                            const p = produitsFournisseur.find(x => x._id === id);
                            if (!p) return s;

                            const prixUnitaire = getPrixUnitaireAchat(p);
                            if (line && line.mode === 'lineaire') {
                              const lineaire = recalculateLineaireEntry(line);
                              const metres = Number(lineaire.metres) || 0;
                              return s + prixUnitaire * metres;
                            }

                            return s + prixUnitaire * (Number(line && line.qte) || 1);
                          }, 0).toLocaleString()} TND
                        </strong>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label><i className="fas fa-align-left"></i> Description</label>
                    <textarea value={docDescription} onChange={e => setDocDescription(e.target.value)} rows="3" placeholder="Détails du document…" />
                  </div>

                  {docType !== 'bon_commande' && (
                    <div className="form-group">
                      <div className="df-frais-header">
                        <label><i className="fas fa-plus-circle"></i> Frais supplémentaires</label>
                        <button type="button" className="df-btn-add-frais" onClick={addFrais}>+ Ajouter</button>
                      </div>
                      {docFrais.map((f, i) => (
                        <div key={i} className="df-frais-row">
                          <input type="text" placeholder="Libellé (ex: Transport)" value={f.label} onChange={e => updateFrais(i, 'label', e.target.value)} />
                          <input type="number" placeholder="Prix TND" value={f.prix} onChange={e => updateFrais(i, 'prix', e.target.value)} min="0" step="0.001" />
                          <button type="button" className="df-btn-remove-frais" onClick={() => removeFrais(i)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="form-group">
                    <label><i className="fas fa-handshake"></i> Conditions</label>
                    <input type="text" value={docConditions} onChange={e => setDocConditions(e.target.value)} placeholder="Ex: Paiement à 30 jours" />
                  </div>
                  <div className="form-group">
                    <label><i className="fas fa-sticky-note"></i> Notes internes</label>
                    <textarea value={docNotes} onChange={e => setDocNotes(e.target.value)} rows="2" placeholder="Notes visibles uniquement en interne…" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowDocModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit" disabled={docSubmitting}>
                    {docSubmitting ? <><i className="fas fa-spinner fa-spin"></i> Creation...</> : <><i className="fas fa-check"></i> Creer le {docType === 'bon_commande' ? 'BC' : 'BL'}</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* ══════ MODALE : Paiement échéance ══════ */}
        {showEcheanceModal && selectedFacture && selectedEcheance && (
          <div className="modal-overlay" onClick={() => setShowEcheanceModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Payer une échéance</h2>
                <button className="modal-close" onClick={() => setShowEcheanceModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handlePayEcheance}>
                <div className="modal-body">
                  <p><strong>Facture:</strong> {selectedFacture.numeroFacture}</p>
                  <p><strong>Montant:</strong> {(selectedEcheance.montant || 0).toLocaleString()} TND</p>
                  <div className="form-group">
                    <label>Référence paiement</label>
                    <input type="text" value={echeanceForm.reference} onChange={e => setEcheanceForm(prev => ({ ...prev, reference: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Méthode de paiement</label>
                    <select value={echeanceForm.methode} onChange={e => setEcheanceForm(prev => ({ ...prev, methode: e.target.value }))}>
                      {PAYMENT_MODES.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  {echeanceForm.methode === 'especes' && (
                    <div className="form-group">
                      <label>Nom du recepteur</label>
                      <input type="text" value={echeanceForm.nomRecepteur} onChange={e => setEcheanceForm(prev => ({ ...prev, nomRecepteur: e.target.value }))} />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Date réelle du paiement</label>
                    <input type="date" value={echeanceForm.datePaiement} onChange={e => setEcheanceForm(prev => ({ ...prev, datePaiement: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Banque / N° chèque / Référence virement</label>
                    <input type="text" value={echeanceForm.banque} onChange={e => setEcheanceForm(prev => ({ ...prev, banque: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Pièce jointe (fichier)</label>
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={e => setEcheanceForm(prev => ({ ...prev, pieceJointeFile: e.target.files[0] || null }))}
                      style={{ padding: '6px', border: '1px solid #e2e8f0', borderRadius: '8px', width: '100%' }}
                    />
                    {echeanceForm.pieceJointeFile && (
                      <small style={{ color: '#059669', marginTop: '4px', display: 'block' }}>
                        <i className="fas fa-check"></i> {echeanceForm.pieceJointeFile.name}
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Commentaire</label>
                    <textarea rows="3" value={echeanceForm.commentaire} onChange={e => setEcheanceForm(prev => ({ ...prev, commentaire: e.target.value }))} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEcheanceModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit" disabled={submittingPayment}>
                    <i className={`fas ${submittingPayment ? 'fa-spinner fa-spin' : 'fa-check'}`}></i> {submittingPayment ? 'Validation...' : 'Valider paiement'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════ MODALE : Modifier échéance ══════ */}
        {showModifierEcheanceModal && selectedFacture && selectedEcheance && (
          <div className="modal-overlay" onClick={() => setShowModifierEcheanceModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="fas fa-edit" style={{ marginRight: '8px', color: '#f59e0b' }}></i>Modifier l'échéance</h2>
                <button className="modal-close" onClick={() => setShowModifierEcheanceModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleModifierEcheance}>
                <div className="modal-body">
                  <div style={{ background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.875rem', color: '#92400e' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                    Facture <strong>{selectedFacture.numeroFacture}</strong> — Échéance du <strong>{new Date(selectedEcheance.dateEcheance).toLocaleDateString('fr-FR')}</strong>
                  </div>
                  <div className="form-group">
                    <label>Montant (TND) *</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      required
                      value={modifierEcheanceForm.montant}
                      onChange={e => setModifierEcheanceForm(prev => ({ ...prev, montant: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Type de paiement</label>
                    <select value={modifierEcheanceForm.methode} onChange={e => setModifierEcheanceForm(prev => ({ ...prev, methode: e.target.value }))}>
                      {PAYMENT_MODES.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                      <option value="comptant">Comptant</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date d'échéance *</label>
                    <input
                      type="date"
                      required
                      value={modifierEcheanceForm.dateEcheance}
                      onChange={e => setModifierEcheanceForm(prev => ({ ...prev, dateEcheance: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Motif de modification</label>
                    <textarea
                      rows="2"
                      placeholder="Raison de la modification (optionnel)..."
                      value={modifierEcheanceForm.commentaire}
                      onChange={e => setModifierEcheanceForm(prev => ({ ...prev, commentaire: e.target.value }))}
                    />
                  </div>
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '0.8rem', color: '#b91c1c' }}>
                    <i className="fas fa-lock" style={{ marginRight: '6px' }}></i>
                    Une fois l'échéance marquée comme payée, elle ne pourra plus être modifiée.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowModifierEcheanceModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit" style={{ background: '#f59e0b' }}>
                    <i className="fas fa-check"></i> Enregistrer les modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════ MODALE : Évaluation ══════ */}
        {showEvaluationModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Évaluer le Fournisseur</h2>
                <button className="modal-close" onClick={() => setShowEvaluationModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleEvaluationSubmit}>
                <div className="modal-body">
                  <div className="evaluation-form">
                    {[['qualite','Qualité'], ['prix','Prix'], ['delai','Délai'], ['service','Service']].map(([key, label]) => (
                      <div key={key} className="form-group">
                        <label>{label} ({evaluationData[key]}/5)</label>
                        <input type="range" min="0" max="5" step="0.5" value={evaluationData[key]}
                          onChange={e => setEvaluationData({ ...evaluationData, [key]: parseFloat(e.target.value) })} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEvaluationModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit"><i className="fas fa-check"></i> Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════ MODALE : Historique ══════ */}
        {showHistoriqueModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Ajouter une Entrée Historique</h2>
                <button className="modal-close" onClick={() => setShowHistoriqueModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleHistoriqueSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={historiqueData.type} onChange={e => setHistoriqueData({ ...historiqueData, type: e.target.value })} required>
                      {['Commande','Paiement','Livraison','Réclamation','Contact','Évaluation','Modification','Autre'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Titre *</label>
                    <input type="text" value={historiqueData.titre} onChange={e => setHistoriqueData({ ...historiqueData, titre: e.target.value })} required placeholder="Ex: Commande matériel" />
                  </div>
                  <div className="form-group">
                    <label>Description *</label>
                    <textarea value={historiqueData.description} onChange={e => setHistoriqueData({ ...historiqueData, description: e.target.value })} required rows="4" placeholder="Détails de l'événement…" />
                  </div>
                  <div className="form-group">
                    <label>Montant (TND)</label>
                    <input type="number" step="0.001" value={historiqueData.montant} onChange={e => setHistoriqueData({ ...historiqueData, montant: e.target.value })} placeholder="0.000" />
                  </div>
                  <div className="form-group">
                    <label>Statut</label>
                    <select value={historiqueData.statut} onChange={e => setHistoriqueData({ ...historiqueData, statut: e.target.value })}>
                      {['En cours','Terminé','Annulé','En attente'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowHistoriqueModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit"><i className="fas fa-plus"></i> Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════ MODALE : Email ══════ */}
        {showEmailModal && (
          <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header df-email-header">
                <h2><i className="fas fa-envelope"></i> Envoyer par Email</h2>
                <button className="modal-close" onClick={() => setShowEmailModal(false)}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleSendEmail}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Destinataire *</label>
                    <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} required placeholder="email@fournisseur.com" />
                  </div>
                  <div className="form-group">
                    <label>Objet *</label>
                    <input type="text" value={emailObject} onChange={e => setEmailObject(e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows="6" />
                  </div>
                  <div className="df-email-info">
                    <i className="fas fa-paperclip"></i>
                    Le PDF du document <strong>{emailDocNumero}</strong> sera joint automatiquement.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEmailModal(false)}>Annuler</button>
                  <button type="submit" className="btn-submit df-btn-send-email" disabled={emailSending}>
                    {emailSending ? <><i className="fas fa-spinner fa-spin"></i> Envoi…</> : <><i className="fas fa-paper-plane"></i> Envoyer</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modale Historique Prix Devis */}
        <HistoriquePrixModal
          isOpen={showHistoryPriceModal}
          onClose={() => setShowHistoryPriceModal(false)}
          fournisseurId={fournisseurId}
          title={`Historique des prix devis proposé par ${fournisseur?.nom || fournisseur?.raisonSociale || ''}`}
        />

      </div>
    </div>
  );
};

export default DetailFournisseur;
